import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { useAuth } from "@/lib/AuthContext";
import {
  FileText,
  Upload,
  Trash2,
  Download,
  CreditCard,
  Search,
  Eye,
  Printer,
  Receipt,
  ChevronDown,
  ChevronUp,
  Plus,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  PAYMENT_STATUSES,
  PAYMENT_STATUS_VALUES,
  applyReimbursementDelta,
  escapePrintHtml,
  groupStatementsByClerk,
  groupStatementsByStatus,
  isLegacyPaymentStatus,
  normalizePaymentStatus,
  paymentStatusLabel,
  selectReimbursementStatements,
  sumStatementAmounts,
} from "@/lib/clerkStatements";
import { fetchClerkStatements, updateClerkStatementRowStatus } from "@/lib/clerkStatementRecords";
import {
  collectInvoiceNumbers,
  fetchInvoiceMatches,
  fetchInvoiceMatchesLive,
  filedInvoiceMatches,
  mergeMatchMaps,
  unmatchedInvoiceNumbers,
} from "@/lib/clerkInvoiceMatches";
import { isLacwLocalTestMode, lacwLocalTestStore } from "@/lib/lacwLocalTestStore";
import { base44 } from "@/api/base44Client";
import BudgetRecoupPanel from "@/components/feeslip/BudgetRecoupPanel";
import { AddFeeSlipCard, ClerkStatementUploadCard } from "@/components/feeslip/ClerkUploadCards";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TO_SORT_GROUP, feeSlipSummary, formatAud } from "@/lib/feeSlipSummary";
import { useBudgetRecoup } from "@/hooks/useBudgetRecoup";
import { useClaimReceipts } from "@/hooks/useClaimReceipts";
import ClaimReceiptsFolder from "@/components/claims/ClaimReceiptsFolder";
import { receiptsFor } from "@/lib/claimReceipts";
import { useSharedDraftRecord } from "@/hooks/useSharedDraftRecord";
import PartialPaidSplit from "@/components/PartialPaidSplit";
import {
  PARTIAL_SPLIT_FORM_TYPE,
  emptyPartialSplits,
  normalisePartialSplits,
  partialSplit,
  partialSplitDraftId,
  setPartialSplit,
} from "@/lib/partialPaid";
import { buildRecoupRows, recoupStages, setEntryStage, summariseRecoup } from "@/lib/budgetRecoup";
import { unifyClerkNames } from "@/lib/clerkMatching";
import { matchesFeeSlipSearch } from "@/lib/feeSlipSearch";
import DismissedMatches from "@/components/feeslip/DismissedMatches";
import { DEFAULT_FEE_SLIP_SORT, FEE_SLIP_SORTS, feeSlipComparator } from "@/lib/feeSlipSort";
import { DIRECT_INVOICE_PAYMENTS, PAID_SUMMARY_CLERKS, clerkDisplayName } from "@/lib/clerkDirectory";
import { auDay, statementCover } from "@/lib/statementCover";
import { afterEmailSent, isEmailStatus } from "@/lib/clerkEmail";
import { staysOpenAfterStatus } from "@/lib/feeSlipRows";
import {
  AUTOSAVE_DELAY_MS,
  BLOCKER_MESSAGES,
  autosaveBlocker,
  needsPaymentType,
  rowsToSave,
  saveAllOutcome,
  saveAllQuestion,
  withPaymentType,
  nextAutosave,
  readStoredEdits,
  sameEdit,
  storedEditsKey,
  writeStoredEdits,
} from "@/lib/feeSlipAutosave";
import ClerkEmailComposer from "@/components/feeslip/ClerkEmailComposer";
import FinanceFeeSlipEmail from "@/components/feeslip/FinanceFeeSlipEmail";
import { SEND_TO_FINANCE, afterFinanceSent, attachmentName } from "@/lib/financeFeeSlipEmail";
import { bytesToBase64 } from "@/lib/disbursementEmail";
import { fetchStoredFile } from "@/lib/storedFile";
import {
  isPdfFile,
  statementFileName,
  statementFileRecord,
  statementFileUrl,
} from "@/lib/feeSlipStatements";
import {
  PAYMENT_PERIODS,
  PAYMENT_TYPES,
  amountOwing,
  buildPaymentReportHtml,
  defaultPaymentType,
  defaultSentBy,
  formatDay,
  formatMoney,
  paymentTypeLabel,
  paymentsForRow,
  paymentsInPeriod,
  periodRange,
  shiftPeriod,
  startOfWeek,
  summarisePayments,
  todayIso,
} from "@/lib/paymentReport";
import { fetchFeeSlipPayments, recordFeeSlipPayment, voidFeeSlipPayment } from "@/lib/feeSlipPayments";
import {
  archivableRows,
  archiveFeeSlips,
  deleteFeeSlips,
  fetchArchivedFeeSlips,
  isArchived,
  paidLines,
  unarchiveFeeSlip,
  undeleteFeeSlip,
  unpaidTotal,
  withoutArchived,
  withoutDeleted,
} from "@/lib/feeSlipArchive";
import {
  EMAIL_INTAKE_BRANDS,
  EMAIL_KIND_LABELS,
  EMAIL_ONLY_GROUPS,
  CLERK_FROM_NOTES,
  emailIntakeGroup,
  fetchDismissedEmailIntake,
  fetchEmailIntake,
  toFeeSlipRow,
  updateEmailIntake,
} from "@/lib/emailIntake";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const YEARS = ["2025", "2026", "2027", "2028"];

const BRAND_DETAILS = {
  lacw: {
    // The firm's own name, as its tiles and its portal use it. This now reaches the firm at
    // the top of a printed payments report, so it has to be right.
    orgName: "Law & Advocacy Centre for Women (LACW)",
    appPrefix: "LACW",
    textColor: "text-teal-700",
    bgHeader: "from-teal-700 to-teal-650",
    bgButton: "bg-teal-700 hover:bg-teal-800 focus:ring-teal-500",
    accentColor: "#0f766e"
  },
  eas: {
    orgName: "EAS Legal (EAS)",
    appPrefix: "EAS",
    textColor: "text-amber-800",
    bgHeader: "from-slate-900 to-slate-800",
    bgButton: "bg-amber-700 hover:bg-amber-800 focus:ring-amber-500",
    accentColor: "#C29A5B"
  },
  rga: {
    orgName: "Robyn Greensill & Associates (RGA)",
    appPrefix: "RGA",
    textColor: "text-indigo-700",
    bgHeader: "from-indigo-700 to-indigo-600",
    bgButton: "bg-indigo-700 hover:bg-indigo-800 focus:ring-indigo-500",
    accentColor: "#4338ca"
  },
  vals: {
    orgName: "Victorian Aboriginal Legal Service (VALS)",
    appPrefix: "VALS",
    textColor: "text-rose-700",
    bgHeader: "from-rose-700 to-rose-600",
    bgButton: "bg-rose-700 hover:bg-rose-800 focus:ring-rose-500",
    accentColor: "#e11d48"
  }
};

const DEADLINE_API_BASE = import.meta.env.VITE_DEADLINE_GUARD_API_BASE || "";
// Payment/action statuses, the reimbursement flag and the print helpers live in
// @/lib/clerkStatements so they can be tested directly rather than through the component.

// "VALS to be reimbursed" marks an invoice the firm paid on VALS's behalf and expects back.
// It is deliberately not stored with the payment status: status is written to two different
// back ends depending on the invoice's origin - uploaded invoices to vals_drafts, fee slips
// PATCHed to the Render service - and Render's record has no field for this flag. An unknown
// field sent there would most likely be accepted and silently dropped, so the tick would
// appear to save and then vanish on reload. The flags therefore live in one shared
// vals_drafts record per brand, keyed by invoice id, which behaves the same for both sources.
const REIMBURSEMENT_FORM_TYPE = "clerk_reimbursement_flags";
const REIMBURSEMENT_DRAFT_IDS = {
  vals: "00000000-0000-4000-8000-00000000c1a0",
  lacw: "00000000-0000-4000-8000-00000000c1a1",
  rga: "00000000-0000-4000-8000-00000000c1a2",
  eas: "00000000-0000-4000-8000-00000000c1a3",
  admin: "00000000-0000-4000-8000-00000000c1a4",
};
const reimbursementDraftId = (brand) => REIMBURSEMENT_DRAFT_IDS[brand] || REIMBURSEMENT_DRAFT_IDS.lacw;

const REIMBURSEMENT_LABEL = "VALS to be reimbursed";

const PRINT_LAYOUTS = [
  { value: "grouped", label: "Grouped by status" },
  { value: "table", label: "Table / sheet" },
  // Produces the reimbursement claim on its own: only the marked invoices, every clerk,
  // no status groups. This is the document that goes to VALS.
  { value: "reimbursement", label: `${REIMBURSEMENT_LABEL} only` },
];

export default function FeeSlipInvoicePayment({ isPortalMode = false, brandType = "lacw", isAdminMode = false }) {
  const { user } = useAuth();
  // Archiving and deleting are the operator's, like recording a payment (api/fee-slip-archive.js).
  const canArchive = isAdminMode || user?.role === "admin";
  // The name under "Kind regards," in a clerk email: the first name of whoever is signed in.
  const emailSignOff = String(user?.full_name || user?.name || "").trim().split(/\s+/)[0] || "Emma";
  const { drafts, fetchDrafts, saveDraft, deleteDraft, isLoading } = useCloudDrafts();
  // Counsel invoices were read through useCloudDrafts, which despite the name reads the
  // browser's own storage - so this screen showed only backsheets cached in whichever
  // browser you happened to be sitting at, and silently missed every invoice submitted from
  // anyone else's machine. BacksheetToCounsel writes them to the shared database via
  // /api/vals-drafts, so that is where they are read from now.
  const [backsheetDrafts, setBacksheetDrafts] = useState([]);
  const [backsheetError, setBacksheetError] = useState(false);
  const brandObj = BRAND_DETAILS[brandType] || BRAND_DETAILS.lacw;

  // "VALS to be reimbursed" marks an invoice a firm paid on VALS's behalf and expects back
  // from VALS. LACW has no such arrangement, so on LACW's screen the tick, its badge, its
  // print layout, its totals and its column are not shown, and its marks are never loaded.
  const showsReimbursement = brandType !== "lacw";

  // "Recover from aid" is different: an outside invoice the firm itself paid from its own
  // budget and is getting back through retrospective aid. Every firm can have those, LACW
  // included, so it shows for all of them. See src/lib/budgetRecoup.js.
  const recoup = useBudgetRecoup(brandType, `${brandObj.appPrefix} Recover from aid`);
  // ATLAS claim receipts kept against fee slips, shared with the Disbursements page
  // (src/lib/claimReceipts.js, Emma 26 Oct 2026).
  const claimReceipts = useClaimReceipts(brandType, brandObj.appPrefix);
  const recoupStageOptions = useMemo(() => recoupStages(brandObj.appPrefix), [brandObj.appPrefix]);
  const recoupUserName = user?.full_name || user?.name || user?.email || "";

  // Partial Paid: how much was claimed from VLA and how much is left to pay from the budget,
  // per invoice, in one shared record per brand (src/lib/partialPaid.js).
  const partialSplits = useSharedDraftRecord({
    brand: brandType,
    formType: PARTIAL_SPLIT_FORM_TYPE,
    draftId: partialSplitDraftId(brandType),
    draftName: `${brandObj.appPrefix} Partial paid amounts`,
    empty: emptyPartialSplits,
    normalise: normalisePartialSplits,
    label: "Partial paid amounts",
  });
  const savePartialSplit = async (invoiceItem, fields) => {
    try {
      await partialSplits.change((current) => setPartialSplit(current, invoiceItem.id, fields, { by: recoupUserName }));
      toast.success("Partial paid amounts saved.");
    } catch (err) {
      toast.error(err.message || "Partial paid amounts could not be saved.");
    }
  };
  const setRecoupStage = async (invoiceItem, stage) => {
    try {
      await recoup.change((current) => setEntryStage(current, invoiceItem, stage, { by: recoupUserName }));
      toast.success(stage ? "Recover from aid updated." : "No longer tracked for recovery.");
    } catch (err) {
      toast.error(err.message || "Recover from aid could not be saved.");
    }
  };

  // Form states (Admin mode only)
  const [statementType, setStatementType] = useState("clerk_fee_slip"); // "clerk_fee_slip" or "invoice_payment"
  const [selectedClerk, setSelectedClerk] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [notes, setNotes] = useState("");
  
  // File upload state (Admin mode only)
  const [attachedFile, setAttachedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  // The fee slip list's own search; searchQuery above belongs to the Paid summaries tab.
  const [feeSlipSearch, setFeeSlipSearch] = useState("");
  const searchingFeeSlips = feeSlipSearch.trim() !== "";
  const [typeFilter, setTypeFilter] = useState("all");
  const [clerkFilter, setClerkFilter] = useState("all");

  const [expandedId, setExpandedId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [individualFeeSlips, setIndividualFeeSlips] = useState([]);
  const [clerkStatementRows, setClerkStatementRows] = useState([]);
  const [clerkStatementsError, setClerkStatementsError] = useState(false);
  const [emailItems, setEmailItems] = useState([]);
  // Items a person dismissed. Read only while a search is open (admin), so they can be found
  // and restored; never part of the list or any total.
  const [dismissedEmailItems, setDismissedEmailItems] = useState([]);
  const [restoringId, setRestoringId] = useState("");
  // "Send to Finance for Payment": the fee slips ticked for one email to VALS finance.
  const [financeTicked, setFinanceTicked] = useState(() => new Set());
  const [financeEmailOpen, setFinanceEmailOpen] = useState(false);
  // "From email - check type": ticked lines, to be sorted together (Emma, 29 Oct 2026: over
  // 200 of them, nearly all fee slips).
  const [sortTicked, setSortTicked] = useState(() => new Set());
  const [bulkSorting, setBulkSorting] = useState("");
  // Order within each status group; the printed selection follows it.
  const [feeSlipSort, setFeeSlipSort] = useState(DEFAULT_FEE_SLIP_SORT);
  const [emailIntakeError, setEmailIntakeError] = useState(false);
  // Payments recorded on this screen, and the report built from them. Nothing else records
  // when an invoice was paid or how much of it, which is what a firm asks for.
  const [payments, setPayments] = useState([]);
  const [paymentsError, setPaymentsError] = useState(false);
  // Paid lines archived once the statement showing them was printed (src/lib/feeSlipArchive.js),
  // and the paid lines of the list last printed, to offer to archive.
  const [archivedIds, setArchivedIds] = useState(() => new Map());
  const [archivedError, setArchivedError] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [printedPaid, setPrintedPaid] = useState(null);
  // Lines deleted from this screen; an emailed line is dismissed instead (src/lib/feeSlipArchive.js).
  const [deletedIds, setDeletedIds] = useState(() => new Map());
  const [reportPeriod, setReportPeriod] = useState("monthly");
  const [reportAnchor, setReportAnchor] = useState(() => todayIso());
  // The clerk is chosen first; status groups are then shown within that clerk only.
  const [selectedClerkName, setSelectedClerkName] = useState("");
  const [invoiceMatches, setInvoiceMatches] = useState(new Map());
  const [matchesLoading, setMatchesLoading] = useState(false);
  // Invoice numbers already put through the live Gmail search this session. A number that
  // came back with nothing is not asked about again: the answer would be the same, and the
  // service runs these against one Gmail account. Cleared by a reload, which is the point
  // at which a document that has since arrived would be worth looking for.
  const liveSearchedRef = useRef(new Set());
  // Changes to a row save on their own and are kept in the tab until they have
  // (src/lib/feeSlipAutosave.js), so a reload no longer loses them.
  const [paymentEdits, setPaymentEdits] = useState(() => readStoredEdits(storedEditsKey(brandType)));
  const restoredEditsRef = useRef(Object.keys(paymentEdits).length);
  // Per row: { state: "saving" | "saved" | "error", message?, edit? }.
  const [rowSaveState, setRowSaveState] = useState({});
  // One save at a time: rows of one statement are saved into the same record.
  const autosaveBusyRef = useRef(false);
  // Rows with a save under way. Pressing Save twice, or Save while the row autosaved, ran two
  // saves at once and recorded one payment twice (16 Oct 2026). A ref, not state: two clicks
  // land before the page has redrawn with the button disabled.
  const savingRowsRef = useRef(new Set());
  // "Save all" is under way: it saves one row at a time, and autosave waits until it is done.
  const savingAllRef = useRef(false);
  const [savingAll, setSavingAll] = useState(false);
  const [askSaveAllType, setAskSaveAllType] = useState(false);
  const [autosaveTick, setAutosaveTick] = useState(0);
  const [statusFilters, setStatusFilters] = useState(PAYMENT_STATUS_VALUES);
  const [printLayout, setPrintLayout] = useState("grouped");
  // A fee slip is collapsed to one line by default - who it is for, its status, the client
  // and the amount. A clerk scanning 79 of them wants a list they can read down, not 79
  // open records; the whole row opens, details and payment form together, for the one being
  // worked on.
  const [openRowIds, setOpenRowIds] = useState([]);
  const [reimbursedIds, setReimbursedIds] = useState([]);
  const [reimbursementLoadError, setReimbursementLoadError] = useState(false);
  const [reimbursementSaveError, setReimbursementSaveError] = useState(false);
  // updated_at of the flags record as last read, so a concurrent tick by another clerk is
  // detected by the server rather than silently overwritten.
  const reimbursementVersionRef = useRef(null);

  const fetchBacksheets = useCallback(async () => {
    // Local test mode is the fictional-data demo, and it is excluded on production
    // hostnames. Reading the local store here keeps the demo working now that the real
    // path goes to the shared database - without it this screen is simply empty offline.
    if (brandType === "lacw" && isLacwLocalTestMode()) {
      setBacksheetDrafts(lacwLocalTestStore.listDrafts("backsheet"));
      setBacksheetError(false);
      return;
    }
    try {
      const params = new URLSearchParams({ brand: brandType, form_type: "backsheet" });
      const response = await fetch(`/api/vals-drafts?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error(`Counsel invoices returned ${response.status}`);
      const payload = await response.json();
      setBacksheetDrafts(Array.isArray(payload) ? payload : payload?.drafts || []);
      setBacksheetError(false);
    } catch (error) {
      console.warn("Counsel invoices could not be loaded:", error);
      setBacksheetError(true);
    }
  }, [brandType]);

  // Saved clerk statements from the Render service - the authoritative outstanding-fees
  // position, parsed from each clerk's statement PDF and annotated by clerk and solicitor.
  const fetchClerkStatementRows = useCallback(async () => {
    try {
      setClerkStatementRows(await fetchClerkStatements(brandType));
      setClerkStatementsError(false);
    } catch (error) {
      console.warn("Clerk statements could not be loaded:", error);
      setClerkStatementsError(true);
    }
  }, [brandType]);

  const fetchIndividualFeeSlips = useCallback(async () => {
    try {
      const response = await fetch(`${DEADLINE_API_BASE}/api/fee-slips?client_brand=${encodeURIComponent(brandType)}&limit=500`, {
        cache: "no-store"
      });
      const payload = await response.json();
      if (!response.ok || payload.ok === false) throw new Error(payload.error || "Failed to load fee slips");
      setIndividualFeeSlips(payload.fee_slips || []);
    } catch (error) {
      console.error("Failed to load individual fee slips:", error);
    }
  }, [brandType]);

  // Fee slips and invoices filed from email by the intake job. Only LACW and VALS have it.
  const fetchEmailItems = useCallback(async () => {
    if (!EMAIL_INTAKE_BRANDS.includes(brandType)) return;
    try {
      setEmailItems(await fetchEmailIntake(brandType));
      setEmailIntakeError(false);
    } catch (error) {
      console.warn("Emailed fee slips could not be loaded:", error);
      setEmailIntakeError(true);
    }
  }, [brandType]);

  const fetchDismissedEmailItems = useCallback(async () => {
    if (!canArchive || !EMAIL_INTAKE_BRANDS.includes(brandType)) return;
    try {
      setDismissedEmailItems(await fetchDismissedEmailIntake(brandType));
    } catch (error) {
      console.warn("Dismissed emailed items could not be loaded:", error);
    }
  }, [brandType, canArchive]);

  const fetchPayments = useCallback(async () => {
    try {
      setPayments(await fetchFeeSlipPayments(brandType));
      setPaymentsError(false);
    } catch (error) {
      console.warn("Recorded payments could not be loaded:", error);
      setPaymentsError(true);
    }
  }, [brandType]);

  // Until these load, or if they cannot, every paid line is listed: nothing is hidden on a guess.
  const fetchArchived = useCallback(async () => {
    try {
      const { archived, deleted } = await fetchArchivedFeeSlips(brandType);
      setArchivedIds(archived);
      setDeletedIds(deleted);
      setArchivedError(false);
    } catch (error) {
      console.warn("Archived fee slips could not be loaded:", error);
      setArchivedError(true);
    }
  }, [brandType]);

  useEffect(() => {
    return () => {
      // Only an older inline summary ever produced a blob URL to release; a stored one is
      // just a path through /api/files.
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleToggleExpand = (item) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      // Only an older inline summary ever produced a blob URL to release; a stored one is
      // just a path through /api/files. The preview is cleared either way, or closing a
      // stored summary would leave it on screen.
      if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
      return;
    }

    setExpandedId(item.id);

    // A stored summary is read through /api/files, which checks the session and the firm on
    // every read; an older one carries its own inline copy. Either way the viewer is handed
    // a URL rather than the page rebuilding the bytes.
    const url = statementFileUrl(item.form_data);
    setPreviewUrl(url);
    if (!url) toast.error("This summary has no PDF attached.");
  };

  // Load existing statement summaries on mount
  useEffect(() => {
    fetchDrafts("fee_slip_invoice_payment", brandType);
    fetchBacksheets();
    fetchIndividualFeeSlips();
    fetchClerkStatementRows();
    const refresh = () => fetchIndividualFeeSlips();
    const intervalId = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refresh);
    };
  }, [fetchDrafts, fetchBacksheets, fetchIndividualFeeSlips, fetchClerkStatementRows, brandType]);

  // Opened and on focus, deliberately not on the 30-second timer above: the intake only
  // files every 15 minutes, and that timer is how this screen once ran a Gmail search in a
  // loop.
  useEffect(() => {
    fetchEmailItems();
    window.addEventListener("focus", fetchEmailItems);
    return () => window.removeEventListener("focus", fetchEmailItems);
  }, [fetchEmailItems]);

  // Recorded payments, on the same terms: when the screen opens and when it comes back to
  // the front, never on the 30-second timer.
  useEffect(() => {
    fetchPayments();
    window.addEventListener("focus", fetchPayments);
    return () => window.removeEventListener("focus", fetchPayments);
  }, [fetchPayments]);

  // Archived lines, the same.
  useEffect(() => {
    fetchArchived();
    window.addEventListener("focus", fetchArchived);
    return () => window.removeEventListener("focus", fetchArchived);
  }, [fetchArchived]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // The file is only held here until Advise is pressed; it goes to storage then, so a
  // statement of any size can be chosen. It used to be read into a base64 string at this
  // point, which is what the draft record then had to carry.
  const handleFile = (file) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      return toast.error("Only PDF summary files are supported.");
    }
    setAttachedFile(file);
    toast.success(`Ready to upload: ${file.name}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  // Submit payment advice PDF
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!attachedFile) {
      return toast.error("Please select or drop a PDF statement summary file.");
    }

    if (statementType === "clerk_fee_slip" && !selectedClerk) {
      return toast.error("Please select a Clerk List.");
    }

    const targetClerk = statementType === "clerk_fee_slip" ? selectedClerk : DIRECT_INVOICE_PAYMENTS;
    const draftName = `${statementType === "clerk_fee_slip" ? "Clerk Fee Slip" : "Invoice Payment"} - ${targetClerk} (${selectedMonth} ${selectedYear})`;

    try {
      // The PDF goes straight from the browser to the private portal-uploads bucket, and
      // the record keeps a reference to it. Carrying the file inside the record is what
      // used to fail: a draft is capped at 512 KB, and base64 adds about a third.
      setIsUploading(true);
      const upload = await base44.integrations.Core.UploadFile({ file: attachedFile });

      const newStatement = {
        type: statementType,
        clerkName: targetClerk,
        statementMonth: selectedMonth,
        statementYear: selectedYear,
        ...statementFileRecord(attachedFile, upload),
        notes,
        advisedDate: new Date().toISOString().split("T")[0]
      };

      await saveDraft({
        // No id: each advice is its own record, and the server gives it one. Sending an id
        // means "update that draft", and the made-up "summary_<timestamp>" this used to
        // send is not a uuid, so every upload came back as an internal server error.
        formType: "fee_slip_invoice_payment",
        draftName,
        savedBy: user?.full_name || user?.name || "Admin Registry",
        savedByEmail: user?.email || "admin@completelawsupport.com",
        brand: brandType,
        formData: newStatement,
        matter_id: null
      });

      toast.success("Paid statement summary uploaded and advised successfully!");
      // Reset form
      setAttachedFile(null);
      setSelectedClerk("");
      setNotes("");
    } catch (err) {
      toast.error(err.message || "Failed to upload statement");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete uploaded statement
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this statement record? This will remove it for all lawyers.")) return;
    try {
      await deleteDraft(id, "fee_slip_invoice_payment", brandType);
      toast.success("Statement record deleted.");
    } catch (err) {
      toast.error(err.message || "Failed to delete record");
    }
  };

  // Trigger PDF file download
  const handleDownload = (fd) => {
    const url = statementFileUrl(fd);
    if (!url) return toast.error("PDF data not found.");
    const link = document.createElement("a");
    link.href = url;
    link.download = statementFileName(fd);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered statements list
  const filteredStatements = useMemo(() => {
    return drafts.filter(d => {
      const fd = d.form_data;
      if (!fd) return false;

      // Filter by Type
      if (typeFilter !== "all" && fd.type !== typeFilter) return false;

      // Filter by Clerk
      if (clerkFilter !== "all" && clerkDisplayName(fd.clerkName) !== clerkFilter) return false;

      // Filter by Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const draftName = (d.draft_name || "").toLowerCase();
        const notes = (fd.notes || "").toLowerCase();
        const fileName = (fd.fileName || "").toLowerCase();
        if (!draftName.includes(q) && !notes.includes(q) && !fileName.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [drafts, searchQuery, typeFilter, clerkFilter]);

  const allCounselInvoices = useMemo(() => {
    const uploadedInvoices = (backsheetDrafts || [])
      .filter(d => d.form_data && d.form_data.counsel_invoice)
      .map(d => {
        const fd = d.form_data;
        const inv = fd.counsel_invoice;
        return {
          id: `invoice-${d.id}`,
          sourceId: d.id,
          source: "invoice",
          clientName: `${fd.client_first_name || ""} ${fd.client_surname || ""}`.trim() || "Unnamed Client",
          counselName: fd.counsel_name || "Unknown Counsel",
          clerkName: fd.clerk || "Direct",
          invoiceName: inv.name,
          invoiceSize: inv.size,
          invoiceBase64: inv.base64,
          invoiceAmount: inv.amount || "0.00",
          status: inv.status || "pending",
          notes: inv.payment_notes || "",
          uploadedAt: inv.uploaded_at || d.updated_at,
          // Who sent it in, for the payments report: the person at the firm who submitted
          // the backsheet this invoice came with.
          sentBy: d.saved_by || "",
          rawDraft: d
        };
      });

    const feeSlips = individualFeeSlips.map(item => ({
      id: `fee-slip-${item.id}`,
      sourceId: item.id,
      source: "fee_slip",
      clientName: item.client_name || "Unnamed Client",
      counselName: item.counsel_name || "Counsel not specified",
      clerkName: item.clerk_name || "Direct",
      invoiceName: item.file_name,
      invoiceSize: item.file_size || 0,
      invoiceBase64: null,
      invoiceAmount: item.amount || "0.00",
      invoiceRef: item.invoice_number || "",
      status: item.status || "not_paid",
      notes: item.notes || "",
      uploadedAt: item.created_at || item.updated_at,
      sentBy: item.created_by || "",
      matterId: item.matter_id || ""
    }));

    // An emailed item filed as a disbursement belongs to the Disbursements page, where it is
    // tracked; listing it here as well would give it two statuses.
    const emailRows = emailItems.filter((item) => item.kind !== "disbursement").map(toFeeSlipRow);

    // One list per clerk however each source spells it ("Greens" / "Greens List").
    // One name per clerk: every spelling of a clerk is shown under the name the clerk
    // directory gives it (src/lib/clerkDirectory.js). Clerks it does not know keep the
    // name unifyClerkNames chose for them.
    return unifyClerkNames([...clerkStatementRows, ...feeSlips, ...uploadedInvoices, ...emailRows])
      .map((row) => {
        const name = clerkDisplayName(row.clerkName);
        return name && name !== row.clerkName ? { ...row, clerkName: name } : row;
      });
  }, [backsheetDrafts, individualFeeSlips, clerkStatementRows, emailItems]);

  // A deleted line is gone from the list and every total (Emma, 4 Nov 2026). A search still
  // finds it, under the results, to restore it.
  const counselInvoices = useMemo(
    () => withoutDeleted(allCounselInvoices, deletedIds),
    [allCounselInvoices, deletedIds],
  );

  const currentStatusOf = useCallback(
    (item) => normalizePaymentStatus(paymentEdits[item.id]?.status || item.status),
    [paymentEdits],
  );

  // A clerk's statement covers that clerk's older items: one already on the statement, or
  // dated on or before it and not on it (so paid), is left out of the list and the totals
  // (src/lib/statementCover.js). "Show them" puts them back.
  const [showCovered, setShowCovered] = useState(false);
  // Rows whose email was closed without sending, keyed "<row id>:<status>".
  const [closedEmails, setClosedEmails] = useState(() => new Set());
  const cover = useMemo(() => statementCover(counselInvoices, currentStatusOf), [counselInvoices, currentStatusOf]);
  // Archived paid lines are left out the same way, unless asked for.
  const listedInvoices = useMemo(() => {
    const listed = showCovered ? counselInvoices : cover.visible;
    return showArchived ? listed : withoutArchived(listed, archivedIds, currentStatusOf);
  }, [showCovered, counselInvoices, cover.visible, showArchived, archivedIds, currentStatusOf]);
  const archivedHere = useMemo(
    () => counselInvoices.filter((row) => isArchived(row, archivedIds, currentStatusOf)
      && (!selectedClerkName || row.clerkName === selectedClerkName)).length,
    [counselInvoices, archivedIds, currentStatusOf, selectedClerkName],
  );

  // Search applies first and on its own, so the clerk list is built from everything the
  // search matches. If the status filters narrowed it too, unticking a status would make
  // whole clerks vanish from the picker rather than just emptying their groups.
  //
  // A search looks through everything, including the older items a statement covers: a
  // fee slip being looked for must not be missing just because it was settled
  // (src/lib/feeSlipSearch.js).
  // Read the dismissed items when a search opens, not on every keystroke or refresh.
  useEffect(() => {
    if (searchingFeeSlips) fetchDismissedEmailItems();
  }, [searchingFeeSlips, fetchDismissedEmailItems]);

  const dismissedMatches = useMemo(() => {
    if (!searchingFeeSlips) return [];
    return dismissedEmailItems
      // Belt and braces with the server: only what a person dismissed.
      .filter((item) => item.kind !== "disbursement" && !String(item.dismissed_by || "").startsWith("Email intake"))
      .map(toFeeSlipRow)
      // Spelled as the clerk cards spell it, or a clerk's dismissed items never match the card.
      .map((row) => ({ ...row, clerkName: clerkDisplayName(row.clerkName) || row.clerkName }))
      .filter((row) => !selectedClerkName || row.clerkName === selectedClerkName)
      .filter((row) => matchesFeeSlipSearch(row, feeSlipSearch));
  }, [searchingFeeSlips, dismissedEmailItems, selectedClerkName, feeSlipSearch]);

  const deletedMatches = useMemo(() => {
    // A firm cannot restore a line, so it is not shown one to restore.
    if (!canArchive || !searchingFeeSlips || !deletedIds.size) return [];
    return allCounselInvoices
      .filter((row) => deletedIds.has(row.id))
      .filter((row) => !selectedClerkName || row.clerkName === selectedClerkName)
      .filter((row) => matchesFeeSlipSearch(row, feeSlipSearch))
      .map((row) => ({
        ...row,
        dismissedBy: deletedIds.get(row.id)?.archived_by || "",
        dismissedAt: deletedIds.get(row.id)?.archived_at || "",
      }));
  }, [canArchive, searchingFeeSlips, deletedIds, allCounselInvoices, selectedClerkName, feeSlipSearch]);
  const removedMatches = useMemo(() => [...dismissedMatches, ...deletedMatches], [dismissedMatches, deletedMatches]);

  const searchedCounselInvoices = useMemo(() => {
    const q = feeSlipSearch.trim();
    if (!q) return listedInvoices;
    return counselInvoices.filter((item) => matchesFeeSlipSearch(item, feeSlipSearch));
  }, [listedInvoices, counselInvoices, feeSlipSearch]);

  // Then the chosen clerk, then the status filters. Nothing is listed until a clerk is
  // picked - the screen exists to send one clerk their outstanding fees, and a mixed list
  // of every clerk is not what gets sent. A search is the exception: it lists its matches
  // from every clerk, since the point is finding one fee slip wherever it is.
  const filteredCounselInvoices = useMemo(() => {
    if (!selectedClerkName && !searchingFeeSlips) return [];
    return searchedCounselInvoices.filter(item => {
      if (selectedClerkName && item.clerkName !== selectedClerkName) return false;
      const status = normalizePaymentStatus(paymentEdits[item.id]?.status || item.status);
      // A retired status is never in the filter list, so filtering on it would drop the row
      // from the screen entirely rather than merely leaving it ungrouped.
      const isKnown = PAYMENT_STATUS_VALUES.includes(status);
      if (isKnown && !statusFilters.includes(status)) return false;
      return true;
    });
  }, [searchedCounselInvoices, selectedClerkName, searchingFeeSlips, statusFilters, paymentEdits]);

  const clerkGroups = useMemo(
    () => groupStatementsByClerk(searchedCounselInvoices),
    [searchedCounselInvoices],
  );

  // A clerk's card counts only what is still owed (Emma, 3 Nov 2026); paid lines are noted
  // beside it.
  const clerkTotals = useMemo(() => new Map(clerkGroups.map((group) => {
    const paid = paidLines(group.items, currentStatusOf);
    return [group.clerk, {
      unpaid: group.count - paid.count,
      owing: unpaidTotal(group.items, { statusOf: currentStatusOf, payments }),
      paid: paid.count,
    }];
  })), [clerkGroups, currentStatusOf, payments]);

  // Named selectedClerkGroup, not selectedClerk - the upload form above already owns a
  // state of that name for choosing which clerk a statement is being uploaded for.
  const selectedClerkGroup = useMemo(
    () => clerkGroups.find((group) => group.clerk === selectedClerkName) || null,
    [clerkGroups, selectedClerkName],
  );

  // The page's sections are tabs; the forms for adding things open in a side panel, so they
  // are not all on screen at once.
  const [activeTab, setActiveTab] = useState("invoices");
  const [addForm, setAddForm] = useState(null);

  // The figures across the top: owing, waiting to be sorted, paid this month, to recover.
  const recoupSummary = useMemo(
    () => summariseRecoup(buildRecoupRows(recoup.data, allCounselInvoices), brandObj.appPrefix),
    [recoup.data, allCounselInvoices, brandObj.appPrefix],
  );
  const summary = useMemo(
    () => feeSlipSummary({ rows: cover.visible, payments, statusOf: currentStatusOf, recoup: recoupSummary }),
    [cover.visible, payments, currentStatusOf, recoupSummary],
  );

  // Clerks an emailed fee slip can be filed under: the ones already on screen, spelled as
  // the statements spell them so the slip joins that group, plus the standard lists.
  const clerkChoices = useMemo(
    () => [...new Set([
      ...clerkGroups.map((group) => group.clerk)
        .filter((name) => !EMAIL_ONLY_GROUPS.includes(name) && name !== "Unattributed"),
      ...PAID_SUMMARY_CLERKS.filter((name) => name !== DIRECT_INVOICE_PAYMENTS),
    ])].sort((a, b) => a.localeCompare(b)),
    [clerkGroups],
  );

  const reportRange = useMemo(() => periodRange(reportPeriod, reportAnchor), [reportPeriod, reportAnchor]);
  const reportPayments = useMemo(() => paymentsInPeriod(payments, reportRange), [payments, reportRange]);
  const reportTotals = useMemo(() => summarisePayments(reportPayments), [reportPayments]);

  // Only VALS has the Crim budget email (Emma, 26 Oct 2026). Emma works it from the VALS
  // view (completelawsupport.com/vals-feeslipinvoicepayment) as well as the admin portal, so
  // it shows to an administrator on either; VALS's own staff do not get it - the email goes
  // from the portal's mailbox, which only an administrator may send from.
  const canEmailFinance = brandType === "vals" && (isAdminMode || user?.role === "admin");
  const financeRows = useMemo(
    () => filteredCounselInvoices.filter((item) => financeTicked.has(item.id) && currentStatusOf(item) === SEND_TO_FINANCE),
    [filteredCounselInvoices, financeTicked, currentStatusOf],
  );
  const toggleFinanceTick = (id) => setFinanceTicked((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  // A fee slip's invoice PDF(s), ready to attach: an emailed invoice's stored file, the
  // invoice matched to a statement line, a backsheet invoice's file, or a Deadline Guard fee
  // slip's document. Same-origin reads only (src/lib/storedFile.js).
  const loadFeeSlipAttachments = async (row) => {
    const fromDataUrl = (dataUrl) => {
      const base64 = String(dataUrl).split(",")[1] || "";
      return base64 ? { data: base64, size: Math.floor((base64.length * 3) / 4) } : null;
    };
    const asAttachment = (bytes, index, total) => ({
      filename: attachmentName(row, index, total), mimeType: "application/pdf", data: bytesToBase64(bytes), size: bytes.length,
    });
    if (row.source === "email") {
      if (!row.documentUrl) return [];
      return [asAttachment(await fetchStoredFile(row.documentUrl), 0, 1)];
    }
    if (row.source === "clerk_statement") {
      const matched = attachmentsFor(row);
      const files = [];
      for (const [index, file] of matched.entries()) files.push(asAttachment(await fetchStoredFile(file.url), index, matched.length));
      return files;
    }
    let documentData = row.invoiceBase64;
    if (row.source === "fee_slip") {
      const response = await fetch(`${DEADLINE_API_BASE}/api/fee-slips/${encodeURIComponent(row.sourceId)}`, { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok || payload.ok === false) throw new Error(payload.error || "The fee slip could not be read");
      documentData = payload.fee_slip?.file_data;
    }
    if (!documentData) return [];
    if (String(documentData).startsWith("data:")) {
      const decoded = fromDataUrl(documentData);
      return decoded ? [{ filename: attachmentName(row), mimeType: "application/pdf", ...decoded }] : [];
    }
    return [asAttachment(await fetchStoredFile(documentData), 0, 1)];
  };

  // Sent: each fee slip becomes Sent to Finance with the date in its notes, and saves itself.
  const handleFinanceSent = ({ to, cc }) => {
    setPaymentEdits((current) => {
      const next = { ...current };
      for (const row of financeRows) {
        next[row.id] = {
          ...next[row.id],
          ...afterFinanceSent({ notes: current[row.id]?.notes ?? row.notes ?? "", to, cc }),
        };
      }
      return next;
    });
    setFinanceTicked(new Set());
    setFinanceEmailOpen(false);
  };

  const claimReceiptsFor = useCallback(
    (rowId) => receiptsFor(claimReceipts.data, "fee_slip", rowId),
    [claimReceipts.data],
  );
  // What a claim receipt can be kept against: every fee slip on the page.
  const claimCandidates = useMemo(() => counselInvoices.map((row) => {
    const number = String(row.invoiceRef || row.invoiceName || "").trim();
    const amount = Number.parseFloat(row.invoiceAmount) || 0;
    return {
      kind: "fee_slip",
      id: row.id,
      label: [row.clientName, row.clerkName, number && `invoice ${number}`, amount ? `$${amount.toFixed(2)}` : ""].filter(Boolean).join(" · "),
      clientName: row.clientName,
      invoiceRef: number,
      amount,
    };
  }), [counselInvoices]);

  // Sorting emailed items is an administrator's job (the API refuses anyone else); Emma does it
  // from the admin portal and from the VALS view alike.
  const canSortEmails = isAdminMode || user?.role === "admin";
  const sortingCheckType = canSortEmails && selectedClerkName === TO_SORT_GROUP;
  const sortRows = useMemo(
    () => (sortingCheckType ? filteredCounselInvoices.filter((row) => row.source === "email" && row.emailKind === "check_type") : []),
    [sortingCheckType, filteredCounselInvoices],
  );
  const sortChosen = sortRows.filter((row) => sortTicked.has(row.id));
  const allSortTicked = sortRows.length > 0 && sortChosen.length === sortRows.length;

  // Marks the ticked lines fee slips or disbursements, a few at a time. A fee slip from a
  // clerk's accounts address goes straight to that clerk's list; a disbursement goes to the
  // Disbursements page's "To bring in".
  const bulkSetKind = async (kind) => {
    const rows = sortChosen;
    if (!rows.length) return;
    const word = kind === "fee_slip" ? "fee slip" : "disbursement";
    if (!window.confirm(`Mark ${rows.length} emailed item${rows.length === 1 ? "" : "s"} as ${word}s?`)) return;
    let done = 0;
    let failed = 0;
    const queue = [...rows];
    const worker = async () => {
      while (queue.length) {
        const row = queue.shift();
        try {
          await updateEmailIntake(row.sourceId, { kind });
          done += 1;
        } catch {
          failed += 1;
        }
        setBulkSorting(`Marking ${done + failed} of ${rows.length}…`);
      }
    };
    setBulkSorting(`Marking 0 of ${rows.length}…`);
    await Promise.all([worker(), worker(), worker(), worker()]);
    await fetchEmailItems();
    // Sorted items arrive in their new lists closed.
    const moved = new Set(rows.map((row) => row.id));
    setOpenRowIds(current => current.filter(value => !moved.has(value)));
    setSortTicked(new Set());
    setBulkSorting("");
    if (failed) toast.error(`${done} marked as ${word}s; ${failed} could not be saved - try those again.`);
    else toast.success(`${done} marked as ${word}s.`);
  };

  // Paid to the firm instead of counsel: still to be paid on to the barrister.
  const payCounselRows = useMemo(
    () => counselInvoices.filter((row) => currentStatusOf(row) === "paid_to_firm"),
    [counselInvoices, currentStatusOf],
  );

  // A fee slip opened from the morning summary: its clerk is chosen, its status group shown
  // and the fee slip opened, then it is scrolled to.
  const focus = usePortalFocus("fee-slip", brandType);
  const [focusShown, setFocusShown] = useState(false);
  useEffect(() => {
    if (!focus || focusShown) return;
    const row = counselInvoices.find((item) => item.id === focus.id);
    if (!row) return;
    const status = currentStatusOf(row);
    setFocusShown(true);
    setSelectedClerkName(row.clerkName);
    setStatusFilters((current) => (current.includes(status) ? current : [...current, status]));
    setOpenRowIds((current) => (current.includes(row.id) ? current : [...current, row.id]));
  }, [focus, focusShown, counselInvoices, currentStatusOf]);
  useScrollToFocus(focus?.id, focusShown);

  const groupedCounselInvoices = useMemo(
    () => groupStatementsByStatus(filteredCounselInvoices, currentStatusOf, feeSlipComparator(feeSlipSort)),
    [filteredCounselInvoices, currentStatusOf, feeSlipSort],
  );

  // Every invoice flagged for reimbursement, across ALL clerks and ignoring the status and
  // search filters. This is the "Other Fee Slips" set: it has to be the full picture of
  // what VALS owes back, not whatever happens to be on screen.
  const reimbursementStatements = useMemo(
    () => selectReimbursementStatements(counselInvoices, reimbursedIds),
    [counselInvoices, reimbursedIds],
  );

  const reimbursementTotal = useMemo(
    () => sumStatementAmounts(reimbursementStatements),
    [reimbursementStatements],
  );

  // The fee slip list refreshes every 30 seconds, and each refresh builds a new group object
  // even when not a single row has changed. Keying the lookup on the invoice numbers
  // themselves rather than on that object is what stops the refresh restarting the search:
  // when it was keyed on the object, every refresh kicked off another round of live Gmail
  // queries, forever, for as long as a clerk was selected.
  // Statement rows whose invoice the email intake has already filed: shown straight away and
  // never searched for. Keyed on the numbers too, for the same reason as matchKey below.
  const filedMatches = useMemo(() => filedInvoiceMatches(counselInvoices), [counselInvoices]);
  const filedKey = useMemo(() => [...filedMatches.keys()].sort().join(","), [filedMatches]);

  const matchKey = useMemo(
    // An emailed row's "invoice name" is its file name, not an invoice number - searching
    // Gmail for it would only find the email it came from.
    () => {
      if (!selectedClerkGroup) return "";
      const filed = new Set(filedKey ? filedKey.split(",") : []);
      return collectInvoiceNumbers(selectedClerkGroup.items.filter((item) => item.source !== "email"))
        .filter((number) => !filed.has(number))
        .join(",");
    },
    [selectedClerkGroup, filedKey],
  );

  // Look up the actual invoice documents for the chosen clerk only. Matching every clerk at
  // once would send hundreds of invoice numbers for rows nobody is looking at.
  //
  // Two passes, in this order. The database lookup answers in one request and renders
  // immediately. Whatever it could not place then goes to the live Gmail search, in chunks,
  // and lands as it arrives. Without the second pass this screen attached fewer documents
  // than the Client Account dashboard, which has always done the live search - same
  // invoices, same mailbox, fewer of them found here.
  useEffect(() => {
    const numbers = matchKey ? matchKey.split(",") : [];
    if (!numbers.length) return undefined;

    let cancelled = false;
    setMatchesLoading(true);

    (async () => {
      let matches = new Map();
      try {
        matches = await fetchInvoiceMatches(numbers);
        if (cancelled) return;
        setInvoiceMatches(matches);
      } catch (error) {
        console.warn("Invoice matching failed:", error);
        // Left silent on screen: no match simply means no document was found, which is the
        // normal case for many rows, so an error banner here would cry wolf. The live pass
        // still runs - a database outage is exactly when Gmail is the only source left.
      }

      const missing = unmatchedInvoiceNumbers(numbers, matches)
        .filter((number) => !liveSearchedRef.current.has(number));
      if (!missing.length) return;
      missing.forEach((number) => liveSearchedRef.current.add(number));

      try {
        await fetchInvoiceMatchesLive(missing, {
          onChunk: (found) => {
            if (cancelled) return;
            setInvoiceMatches((current) => mergeMatchMaps(current, found));
          },
        });
      } catch (error) {
        console.warn("Live invoice matching failed:", error);
      }
    })()
      .finally(() => { if (!cancelled) setMatchesLoading(false); });

    return () => { cancelled = true; };
  }, [matchKey]);

  const attachmentsFor = useCallback(
    (item) => {
      const number = String(item?.invoiceName || "").trim();
      return filedMatches.get(number) || invoiceMatches.get(number) || [];
    },
    [filedMatches, invoiceMatches],
  );

  const handlePrintSelection = () => {
    const reimbursementOnly = showsReimbursement && printLayout === "reimbursement";
    if (reimbursementOnly && !reimbursementStatements.length) {
      toast.error(`No invoices are marked "${REIMBURSEMENT_LABEL}" yet.`);
      return;
    }
    if (!reimbursementOnly && !filteredCounselInvoices.length && !reimbursementStatements.length) {
      toast.error("Choose a clerk and at least one status group before printing.");
      return;
    }
    const selectedLabels = PAYMENT_STATUSES
      .filter(option => statusFilters.includes(option.value))
      .map(option => option.label);
    const money = (value) => (Number.parseFloat(value) || 0)
      .toLocaleString("en-AU", { style: "currency", currency: "AUD" });
    // Only what is still owed is totalled. Paid lines are listed for reference and noted apart.
    const unpaid = unpaidTotal(filteredCounselInvoices, { statusOf: currentStatusOf, payments });
    const paid = paidLines(filteredCounselInvoices, currentStatusOf);

    // The reimbursement flag has to survive onto the printed page - that is the whole point
    // of marking it - so it gets its own column rather than only a screen badge.
    // Notes print as they stand on screen, so a note still saving is not left off.
    const row = (item, { withStatus = true } = {}) => `
      <tr${showsReimbursement && isReimbursed(item.id) ? ' class="reimburse"' : ""}>
        <td>${escapePrintHtml(item.clientName)}</td>
        <td>${escapePrintHtml(item.counselName)}</td>
        <td>${escapePrintHtml(item.clerkName)}</td>
        <td>${escapePrintHtml(item.invoiceName)}</td>
        ${withStatus ? `<td>${escapePrintHtml(paymentStatusLabel(currentStatusOf(item)))}</td>` : ""}
        ${showsReimbursement ? `<td class="flag">${isReimbursed(item.id) ? "YES" : ""}</td>` : ""}
        <td>${escapePrintHtml(attachmentsFor(item).map(a => a.filename).join(", "))}</td>
        <td class="amount">${escapePrintHtml(money(item.invoiceAmount))}</td>
        <td class="notes">${escapePrintHtml(paymentEdits[item.id]?.notes ?? item.notes)}</td>
      </tr>`;

    const head = (withStatus = true) => `<thead><tr>
      <th>Client</th><th>Counsel</th><th>Clerk/List</th><th>Document</th>
      ${withStatus ? "<th>Status</th>" : ""}
      ${showsReimbursement ? `<th>${escapePrintHtml(REIMBURSEMENT_LABEL)}</th>` : ""}<th>Invoice document</th><th>Amount</th><th class="notes">Notes</th>
    </tr></thead>`;

    // The reimbursement claim: only the marked invoices, every clerk, grouped by clerk with
    // a subtotal each, because VALS needs to see which list each fee sits with. No status
    // groups and no clerk selection - this is a claim, not a review of one clerk's account.
    if (reimbursementOnly) {
      const byClerk = groupStatementsByClerk(reimbursementStatements);
      const claimBody = byClerk.map(group => `
        <h2>${escapePrintHtml(group.clerk)} <span class="count">(${group.items.length})</span></h2>
        <table>${head()}<tbody>${group.items.map(item => row(item)).join("")}</tbody></table>
        <div class="subtotal">${escapePrintHtml(group.clerk)} subtotal: ${escapePrintHtml(money(group.total))}</div>`).join("");

      const claimWindow = window.open("", "_blank");
      if (!claimWindow) {
        toast.error("The print window was blocked. Please allow pop-ups and try again.");
        return;
      }
      claimWindow.opener = null;
      claimWindow.document.write(`<!doctype html><html><head><title>${escapePrintHtml(brandObj.appPrefix)} - ${escapePrintHtml(REIMBURSEMENT_LABEL)}</title><style>
        body{font-family:Arial,sans-serif;color:#172033;margin:28px}
        h1{font-size:22px;margin-bottom:6px}
        h2{font-size:14px;margin:18px 0 6px;padding-bottom:3px;border-bottom:1px solid #cbd5e1}
        .count{color:#64748b;font-weight:400}
        .meta{color:#64748b;font-size:12px;margin-bottom:18px}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;vertical-align:top}
        th{background:#f1f5f9}
        .amount{text-align:right}
        .notes{width:24%;white-space:pre-wrap;overflow-wrap:anywhere}
        .flag{text-align:center;font-weight:700;color:#b45309}
        tr.reimburse{background:#fffbeb}
        .subtotal{margin:6px 0 4px;text-align:right;font-weight:700}
        .total{margin-top:18px;text-align:right;font-weight:700;font-size:15px;border-top:2px solid #b45309;padding-top:8px}
        @page{size:A4 landscape;margin:12mm}
      </style></head><body>
        <h1>${escapePrintHtml(brandObj.appPrefix)} &mdash; ${escapePrintHtml(REIMBURSEMENT_LABEL)}</h1>
        <div class="meta">
          <strong>Invoices marked for reimbursement:</strong> ${reimbursementStatements.length} across ${byClerk.length} clerk${byClerk.length === 1 ? "" : "s"}<br>
          <strong>Prepared:</strong> ${escapePrintHtml(new Date().toLocaleString("en-AU"))}
        </div>
        ${claimBody}
        <div class="total">Total to be reimbursed: ${escapePrintHtml(money(reimbursementTotal))}</div>
      </body></html>`);
      claimWindow.document.close();
      return;
    }

    let body;
    if (printLayout === "table") {
      // Flat sheet: one table, every selected statement, sorted by clerk then client.
      const flat = [...filteredCounselInvoices].sort((a, b) =>
        a.clerkName.localeCompare(b.clerkName) || a.clientName.localeCompare(b.clientName));
      body = `<table>${head()}<tbody>${flat.map(item => row(item)).join("")}</tbody></table>`;
    } else {
      // Grouped: a sub-table per status, each with its own subtotal.
      body = groupedCounselInvoices.map(group => {
        const groupTotal = group.status === "paid"
          ? paidLines(group.items, currentStatusOf).total
          : unpaidTotal(group.items, { statusOf: currentStatusOf, payments });
        const subtotal = group.status === "partial_paid" ? "still owing" : "subtotal";
        return `<h2>${escapePrintHtml(group.label)} <span class="count">(${group.items.length})</span></h2>
          <table>${head(false)}<tbody>${group.items.map(item => row(item, { withStatus: false })).join("")}</tbody></table>
          <div class="subtotal">${escapePrintHtml(group.label)} ${subtotal}: ${escapePrintHtml(money(groupTotal))}</div>`;
      }).join("");
    }

    // Other Fee Slips: every flagged invoice across all clerks, regardless of the filters
    // above, so the reimbursement position is complete on its own.
    const otherStatements = showsReimbursement && reimbursementStatements.length
      ? `<h2 class="other">Other Fee Slips &mdash; ${escapePrintHtml(REIMBURSEMENT_LABEL)}</h2>
         <p class="note">All clerks. Independent of the status selection above.</p>
         <table>${head()}<tbody>${reimbursementStatements.map(item => row(item)).join("")}</tbody></table>
         <div class="subtotal">${escapePrintHtml(REIMBURSEMENT_LABEL)} total: ${escapePrintHtml(money(reimbursementTotal))}</div>`
      : "";

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("The print window was blocked. Please allow pop-ups and try again.");
      return;
    }
    printWindow.opener = null;
    printWindow.document.write(`<!doctype html><html><head><title>${escapePrintHtml(brandObj.appPrefix)} Clerk Fee Slips${selectedClerkName ? ` - ${escapePrintHtml(selectedClerkName)}` : ""}</title><style>
      body{font-family:Arial,sans-serif;color:#172033;margin:28px}
      h1{font-size:22px;margin-bottom:6px}
      h2{font-size:14px;margin:18px 0 6px;padding-bottom:3px;border-bottom:1px solid #cbd5e1}
      h2.other{margin-top:26px;border-bottom:2px solid #b45309;color:#b45309;page-break-before:always}
      .count{color:#64748b;font-weight:400}
      .meta{color:#64748b;font-size:12px;margin-bottom:18px}
      .note{color:#64748b;font-size:11px;margin:0 0 8px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th,td{border:1px solid #cbd5e1;padding:8px;text-align:left;vertical-align:top}
      th{background:#f1f5f9}
      .amount{text-align:right}
      .notes{width:24%;white-space:pre-wrap;overflow-wrap:anywhere}
      .flag{text-align:center;font-weight:700;color:#b45309}
      tr.reimburse{background:#fffbeb}
      .subtotal{margin:6px 0 4px;text-align:right;font-weight:700}
      .paid{margin-top:14px;text-align:right;color:#64748b;font-size:12px}
      .total{margin-top:14px;text-align:right;font-weight:700;font-size:13px}
      @page{size:A4 landscape;margin:12mm}
    </style></head><body>
      <h1>${escapePrintHtml(brandObj.appPrefix)} Clerk Fee Slips${selectedClerkName ? ` &mdash; ${escapePrintHtml(selectedClerkName)}` : ""}</h1>
      <div class="meta">
        ${selectedClerkName ? `<strong>Clerk / list:</strong> ${escapePrintHtml(selectedClerkName)}<br>` : ""}
        <strong>Included status groups:</strong> ${escapePrintHtml(selectedLabels.join(", ") || "None")}<br>
        <strong>Layout:</strong> ${escapePrintHtml(PRINT_LAYOUTS.find(o => o.value === printLayout)?.label || printLayout)}<br>
        <strong>Printed:</strong> ${escapePrintHtml(new Date().toLocaleString("en-AU"))}
      </div>
      ${body}
      ${paid.count ? `<div class="paid">Paid, listed for reference: ${paid.count} line${paid.count === 1 ? "" : "s"}, ${escapePrintHtml(money(paid.total))}</div>` : ""}
      <div class="total">Total outstanding: ${escapePrintHtml(money(unpaid))}</div>
      ${otherStatements}
      <script>window.addEventListener("load",()=>window.print());<\/script>
    </body></html>`);
    printWindow.document.close();

    // The paid lines just printed are what the statement needed them for: offer to archive them.
    if (canArchive) {
      const printed = archivableRows(filteredCounselInvoices, { archived: archivedIds, edits: paymentEdits });
      setPrintedPaid(printed.length ? { clerk: selectedClerkName, ids: new Set(printed.map((item) => item.id)) } : null);
    }
  };

  const handleArchivePaid = async (rows, { ask = false } = {}) => {
    if (archiving || !rows.length) return;
    const lines = `${rows.length} paid line${rows.length === 1 ? "" : "s"}`;
    const where = selectedClerkName ? ` for ${selectedClerkName}` : "";
    if (ask && !window.confirm(`Archive the ${lines}${where}?\n\nThey will no longer be listed or counted. Nothing is deleted: a search still finds them, and they can be brought back.`)) {
      return;
    }
    setArchiving(true);
    try {
      await archiveFeeSlips(brandType, rows);
      await fetchArchived();
      setPrintedPaid(null);
      toast.success(`${lines} archived. A search still finds them.`);
    } catch (err) {
      toast.error(err.message || "The paid lines could not be archived.");
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async (invoiceItem) => {
    try {
      await unarchiveFeeSlip(brandType, invoiceItem.id);
      await fetchArchived();
      toast.success("Back in the list.");
    } catch (err) {
      toast.error(err.message || "The line could not be brought back.");
    }
  };

  const loadReimbursementFlags = useCallback(async () => {
    if (!showsReimbursement) return;
    try {
      const params = new URLSearchParams({ brand: brandType, form_type: REIMBURSEMENT_FORM_TYPE });
      const response = await fetch(`/api/vals-drafts?${params}`, { credentials: "include" });
      if (!response.ok) throw new Error(`Reimbursement flags returned ${response.status}`);
      const drafts = await response.json();
      const record = Array.isArray(drafts)
        ? drafts.find(draft => draft.id === reimbursementDraftId(brandType)) || drafts[0]
        : null;
      reimbursementVersionRef.current = record?.updated_at || null;
      setReimbursedIds(Array.isArray(record?.form_data?.ids) ? record.form_data.ids : []);
      setReimbursementLoadError(false);
    } catch (err) {
      console.warn("Reimbursement flags could not be loaded:", err);
      setReimbursementLoadError(true);
    }
  }, [brandType, showsReimbursement]);

  useEffect(() => { loadReimbursementFlags(); }, [loadReimbursementFlags]);

  const isReimbursed = useCallback(
    (invoiceId) => reimbursedIds.includes(invoiceId),
    [reimbursedIds],
  );

  // Applies a single tick/untick and saves it.
  //
  // On a 409 the server is saying another clerk saved first. Because we know exactly which
  // invoice changed, the fix is to re-read their version and re-apply just this one change
  // to it - pushing our whole stale list back would silently undo their marks.
  const setReimbursed = async (invoiceId, shouldReimburse) => {
    let nextIds = applyReimbursementDelta(reimbursedIds, invoiceId, shouldReimburse);
    setReimbursedIds(nextIds);

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const body = {
        id: reimbursementDraftId(brandType),
        form_type: REIMBURSEMENT_FORM_TYPE,
        draft_name: `${brandObj.appPrefix} ${REIMBURSEMENT_LABEL}`,
        brand: brandType,
        form_data: { ids: nextIds },
      };
      if (reimbursementVersionRef.current) body.expected_updated_at = reimbursementVersionRef.current;

      let response;
      try {
        response = await fetch("/api/vals-drafts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } catch (err) {
        console.warn("Reimbursement flag save failed:", err);
        setReimbursementSyncError(true);
        toast.error(`"${REIMBURSEMENT_LABEL}" could not be saved. Check your connection and try again.`);
        return;
      }

      if (response.status === 409) {
        const params = new URLSearchParams({ brand: brandType, form_type: REIMBURSEMENT_FORM_TYPE });
        const refreshed = await fetch(`/api/vals-drafts?${params}`, { credentials: "include" });
        if (!refreshed.ok) break;
        const drafts = await refreshed.json();
        const record = Array.isArray(drafts)
          ? drafts.find(draft => draft.id === reimbursementDraftId(brandType)) || drafts[0]
          : null;
        reimbursementVersionRef.current = record?.updated_at || null;
        nextIds = applyReimbursementDelta(
          Array.isArray(record?.form_data?.ids) ? record.form_data.ids : [],
          invoiceId,
          shouldReimburse,
        );
        setReimbursedIds(nextIds);
        continue;
      }

      if (!response.ok) break;

      const saved = await response.json().catch(() => null);
      reimbursementVersionRef.current = saved?.updated_at || null;
      setReimbursementSaveError(false);
      return;
    }

    console.warn("Reimbursement flag could not be saved after repeated conflicts");
    setReimbursementSaveError(true);
    toast.error(`"${REIMBURSEMENT_LABEL}" could not be saved. Check your connection and try again.`);
  };

  const isRowOpen = (id) => openRowIds.includes(id);

  const toggleRow = (id) => {
    setOpenRowIds(current => current.includes(id)
      ? current.filter(value => value !== id)
      : [...current, id]);
  };

  const toggleStatusFilter = (status) => {
    setStatusFilters(current => current.includes(status)
      ? current.filter(value => value !== status)
      : [...current, status]);
  };

  // The payment a row's Save records, or null when it records none.
  const paymentRecordingFor = (invoiceItem, edit) => {
    const status = edit.status || (invoiceItem.status === "pending" ? "not_paid" : invoiceItem.status);
    if (!isAdminMode || !["paid", "partial_paid"].includes(normalizePaymentStatus(status))) return null;
    return {
      paid_on: edit.paidOn || todayIso(),
      amount_paid: Number(
        edit.amountPaid ?? (normalizePaymentStatus(status) === "paid" ? amountOwing(invoiceItem, payments) : 0),
      ) || 0,
      payment_type: edit.paymentType ?? defaultPaymentType(invoiceItem),
      sent_by: edit.sentBy ?? defaultSentBy(invoiceItem),
    };
  };

  // silent: an autosave - no success message. quiet: one row of "Save all", which reports
  // them together. edit: the change being saved, so that a change made while it saves is
  // kept for the next save rather than cleared. Says what happened, for "Save all".
  const handleUpdatePayment = async (invoiceItem, { edit: givenEdit, silent = false, quiet = false } = {}) => {
    const rowId = String(invoiceItem.id);
    if (savingRowsRef.current.has(rowId)) return { saved: false };
    const edit = givenEdit || paymentEdits[invoiceItem.id] || {};
    const status = edit.status || (invoiceItem.status === "pending" ? "not_paid" : invoiceItem.status);
    const paymentNotes = edit.notes ?? invoiceItem.notes ?? "";

    // A payment is recorded alongside the status, so the report has a date and an amount.
    // Anything wrong with it is caught before the status is saved, rather than leaving the
    // two out of step.
    const owing = amountOwing(invoiceItem, payments);
    // Only Save records a payment; an autosave never does (src/lib/feeSlipAutosave.js).
    const recording = silent ? null : paymentRecordingFor(invoiceItem, edit);

    if (recording?.amount_paid > 0) {
      if (!recording.payment_type) {
        toast.error(`${quiet ? `${invoiceItem.clientName}: ` : ""}Choose whether this is a fee slip or a disbursement invoice before saving.`);
        return { saved: false };
      }
      if (
        owing > 0
        && recording.amount_paid > owing
        && !window.confirm(`${quiet ? `${invoiceItem.clientName}: that` : "That"} is more than the ${formatMoney(owing)} still owing on this invoice. Record it anyway?`)
      ) {
        return { saved: false };
      }
    }

    savingRowsRef.current.add(rowId);
    autosaveBusyRef.current = true;
    setRowSaveState(current => ({ ...current, [rowId]: { state: "saving" } }));
    try {
      if (invoiceItem.source === "fee_slip") {
        const response = await fetch(`${DEADLINE_API_BASE}/api/fee-slips/${encodeURIComponent(invoiceItem.sourceId)}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status, notes: paymentNotes, actor: user?.email || "Portal user" })
        });
        const payload = await response.json();
        if (!response.ok || payload.ok === false) throw new Error(payload.error || "Failed to update fee slip");
        await fetchIndividualFeeSlips();
      } else if (invoiceItem.source === "email") {
        await updateEmailIntake(invoiceItem.sourceId, {
          status,
          notes: paymentNotes,
          ...(edit.clientName !== undefined ? { client_name: edit.clientName } : {}),
          ...(edit.amount !== undefined ? { amount: edit.amount } : {}),
        });
        await fetchEmailItems();
      } else if (invoiceItem.source === "clerk_statement") {
        // A statement row belongs to the stored clerk statement, not to a vals_drafts
        // record. It used to fall through to the branch below and read form_data off a
        // draft that does not exist for it, which is the whole of that error.
        await updateClerkStatementRowStatus({
          recordId: invoiceItem.sourceId,
          invoiceNumber: invoiceItem.invoiceName,
          rowIndex: invoiceItem.rowIndex,
          // The statement stores the human label, which is what it was parsed from.
          statusLabel: paymentStatusLabel(status),
          paymentNotes,
        });
        await fetchClerkStatementRows();
      } else {
      const updatedDraft = {
        ...invoiceItem.rawDraft,
        form_data: {
          ...invoiceItem.rawDraft.form_data,
          counsel_invoice: {
            ...invoiceItem.rawDraft.form_data.counsel_invoice,
            status,
            payment_notes: paymentNotes
          }
        }
      };

      const response = await fetch("/api/vals-drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedDraft)
      });

      if (!response.ok) throw new Error("Failed to update payment status");
        fetchBacksheets();
      }
      let alreadyRecorded = false;
      if (recording?.amount_paid > 0) {
        const recorded = await recordFeeSlipPayment({
          brand: brandType,
          source: invoiceItem.source,
          source_ref: invoiceItem.id,
          client_name: invoiceItem.clientName,
          sent_by: recording.sent_by,
          payment_type: recording.payment_type,
          // An emailed item carries the invoice number read off the document; everywhere
          // else the invoice is named by the row itself.
          invoice_ref: invoiceItem.invoiceRef || invoiceItem.invoiceName,
          clerk_name: invoiceItem.clerkName,
          amount_paid: recording.amount_paid,
          paid_on: recording.paid_on,
          notes: paymentNotes,
        });
        alreadyRecorded = recorded?.duplicate === true;
        await fetchPayments();
      }

      setPaymentEdits(current => {
        if (!(rowId in current) || !sameEdit(current[rowId], edit)) return current;
        const next = { ...current };
        delete next[rowId];
        return next;
      });
      setRowSaveState(current => ({ ...current, [rowId]: { state: "saved" } }));
      if (!silent && !quiet) {
        toast.success(recording?.amount_paid > 0
          ? alreadyRecorded
            ? `That payment of ${formatMoney(recording.amount_paid)} was already recorded, so it was not added again.`
            : `Payment of ${formatMoney(recording.amount_paid)} recorded.`
          : "Payment status and notes updated.");
      }
      return {
        saved: true,
        paid: recording?.amount_paid > 0 && !alreadyRecorded ? recording.amount_paid : 0,
        duplicate: alreadyRecorded,
      };
    } catch (err) {
      console.error(err);
      const message = err.message || "Failed to update payment status.";
      setRowSaveState(current => ({ ...current, [rowId]: { state: "error", message, edit } }));
      toast.error(silent || quiet ? `Not saved (${invoiceItem.clientName}): ${message}` : message);
      return { saved: false };
    } finally {
      savingRowsRef.current.delete(rowId);
      autosaveBusyRef.current = false;
      setAutosaveTick(tick => tick + 1);
    }
  };

  // Every changed line in the list on screen, one after another (one save at a time, above).
  // A change in another clerk's list is left alone: it may be days old, and it is not what
  // the person pressing the button is looking at.
  const saveAllPlans = () => rowsToSave(filteredCounselInvoices, paymentEdits).map((plan) => ({
    ...plan,
    recording: paymentRecordingFor(plan.item, plan.edit),
  }));

  // paymentType: the answer to "what type are these?", for the lines that have none.
  const handleSaveAll = async (paymentType = "") => {
    if (savingAllRef.current) return;
    let plans = saveAllPlans();
    if (!plans.length) return;
    if (paymentType) {
      plans = withPaymentType(plans, paymentType);
      // Kept on the lines too, so that a row's saved change is recognised and cleared.
      setPaymentEdits(current => ({
        ...current,
        ...Object.fromEntries(plans.map((plan) => [plan.item.id, plan.edit])),
      }));
    }
    if (plans.some(needsPaymentType)) {
      setAskSaveAllType(true);
      return;
    }
    setAskSaveAllType(false);
    const question = saveAllQuestion(plans, formatMoney);
    if (question && !window.confirm(question)) return;
    savingAllRef.current = true;
    setSavingAll(true);
    const results = [];
    try {
      while (autosaveBusyRef.current) await new Promise((resolve) => setTimeout(resolve, 100));
      for (const { item, edit } of plans) {
        results.push(await handleUpdatePayment(item, { edit, quiet: true }));
      }
    } finally {
      savingAllRef.current = false;
      setSavingAll(false);
      setAutosaveTick(tick => tick + 1);
    }
    const { message, notSaved } = saveAllOutcome(results, formatMoney);
    if (message) toast.success(message);
    if (notSaved) toast.error(`${notSaved} line${notSaved === 1 ? " was" : "s were"} not saved. They are still changed on the page.`);
  };

  // Keep every unsaved change in the tab until it has been saved.
  useEffect(() => {
    writeStoredEdits(storedEditsKey(brandType), paymentEdits);
  }, [brandType, paymentEdits]);

  useEffect(() => {
    const count = restoredEditsRef.current;
    if (count) toast.info(`${count} change${count === 1 ? "" : "s"} not saved last time ${count === 1 ? "was" : "were"} kept and will save now.`);
  }, []);

  // Save each changed row a moment after the last change to it.
  const invoicesById = useMemo(
    () => new Map(counselInvoices.map(item => [String(item.id), item])),
    [counselInvoices],
  );
  useEffect(() => {
    const failed = new Map(Object.entries(rowSaveState)
      .filter(([, saveState]) => saveState.state === "error")
      .map(([id, saveState]) => [id, saveState.edit]));
    const pending = nextAutosave(paymentEdits, invoicesById, { isAdminMode, failed });
    if (!pending) return undefined;
    const timer = setTimeout(() => {
      if (autosaveBusyRef.current || savingAllRef.current) return;
      handleUpdatePayment(pending.item, { edit: pending.edit, silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // handleUpdatePayment is rebuilt every render; the change it saves is passed in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentEdits, invoicesById, isAdminMode, rowSaveState, autosaveTick]);

  const unsavedHere = rowsToSave(filteredCounselInvoices, paymentEdits).length;
  const unsavedElsewhere = Object.keys(paymentEdits).filter((id) => invoicesById.has(id)).length - unsavedHere;
  const linesNeedingType = askSaveAllType ? saveAllPlans().filter(needsPaymentType).length : 0;

  // Closing or leaving with a change not yet saved asks first.
  useEffect(() => {
    if (!Object.keys(paymentEdits).length) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [paymentEdits]);

  // What a row says about saving: { text, short, tone }, or null when there is nothing to say.
  const rowSaveNote = (inv) => {
    const rowId = String(inv.id);
    const edit = paymentEdits[rowId];
    const saveState = rowSaveState[rowId];
    if (edit) {
      const blocker = autosaveBlocker(inv, edit, { isAdminMode });
      if (blocker) return { text: BLOCKER_MESSAGES[blocker], short: "not saved", tone: "text-amber-700" };
      if (saveState?.state === "error" && sameEdit(saveState.edit, edit)) {
        return { text: `Not saved: ${saveState.message} Press Save to try again.`, short: "not saved", tone: "text-red-700" };
      }
      return { text: "Saving...", short: "saving", tone: "text-slate-500" };
    }
    if (saveState?.state === "saving") return { text: "Saving...", short: "saving", tone: "text-slate-500" };
    if (saveState?.state === "error") return { text: `Not saved: ${saveState.message}`, short: "not saved", tone: "text-red-700" };
    if (saveState?.state === "saved") return { text: "Saved", short: "saved", tone: "text-emerald-700" };
    return null;
  };

  // Type, clerk and dismissal save as soon as they are chosen. Each can move the row to a
  // different group, so the selected group follows it - otherwise it would seem to vanish.
  const handleEmailItemChange = async (invoiceItem, patch, successMessage, toastOptions) => {
    try {
      await updateEmailIntake(invoiceItem.sourceId, patch);
      await fetchEmailItems();
      if (patch.dismissed !== undefined && searchingFeeSlips) await fetchDismissedEmailItems();
      // Moving an item opens its new list, where it arrives closed (Emma, 1 Nov 2026);
      // restoring one is left where the search is.
      if (patch.dismissed === undefined) {
        setSelectedClerkName(emailIntakeGroup({ ...invoiceItem.rawEmail, ...patch }));
        setOpenRowIds(current => current.filter(value => value !== invoiceItem.id));
      }
      toast.success(successMessage, toastOptions);
      return true;
    } catch (error) {
      toast.error(error.message || "The emailed item could not be saved.");
      return false;
    }
  };

  const handleRestoreEmailItem = async (invoiceItem) => {
    setRestoringId(invoiceItem.id);
    await handleEmailItemChange(invoiceItem, { dismissed: false }, "Restored to its list.");
    setRestoringId("");
  };

  // Dismissing hides an item everywhere, so the message carries an Undo for a slip of the
  // hand. A search also finds dismissed items afterwards (DismissedMatches).
  const handleDismissEmailItem = (invoiceItem) => {
    if (!window.confirm("Dismiss this emailed item? It will no longer be listed here. The email itself stays in Gmail.")) return;
    handleEmailItemChange(invoiceItem, { dismissed: true }, "Emailed item dismissed.", {
      duration: 12000,
      action: { label: "Undo", onClick: () => handleRestoreEmailItem(invoiceItem) },
    });
  };

  const handleRestoreLine = async (invoiceItem) => {
    if (invoiceItem.source === "email") {
      await handleRestoreEmailItem(invoiceItem);
      return;
    }
    setRestoringId(invoiceItem.id);
    try {
      await undeleteFeeSlip(brandType, invoiceItem.id);
      await fetchArchived();
      toast.success("Restored to its list.");
    } catch (err) {
      toast.error(err.message || "The line could not be restored.");
    } finally {
      setRestoringId("");
    }
  };

  // Delete takes a line out of the list and every total, whatever its status (Emma, 4 Nov
  // 2026). Nothing is lost: an emailed line is dismissed as before, any other is marked
  // deleted, and a search finds either to restore it.
  const handleDeleteLine = async (invoiceItem) => {
    const amount = Number.parseFloat(invoiceItem.invoiceAmount) || 0;
    if (!window.confirm(`Delete this line: ${invoiceItem.clientName}${amount ? `, ${formatMoney(amount)}` : ""}?\n\nIt will no longer be listed or counted. If you need it back, search for it and press Restore.`)) {
      return;
    }
    const undo = { duration: 12000, action: { label: "Undo", onClick: () => handleRestoreLine(invoiceItem) } };
    if (invoiceItem.source === "email") {
      await handleEmailItemChange(invoiceItem, { dismissed: true }, "Line deleted.", undo);
      return;
    }
    try {
      await deleteFeeSlips(brandType, [invoiceItem]);
      await fetchArchived();
      toast.success("Line deleted.", undo);
    } catch (err) {
      toast.error(err.message || "The line could not be deleted.");
    }
  };

  // A payment entered by mistake is voided rather than deleted, so what was corrected can
  // still be seen.
  const handleVoidPayment = async (payment) => {
    if (!window.confirm(
      `Void the ${formatMoney(payment.amount_paid)} payment recorded on ${formatDay(payment.paid_on)}? It will be left out of the report.`,
    )) return;
    try {
      await voidFeeSlipPayment(payment.id);
      await fetchPayments();
      toast.success("Payment voided.");
    } catch (error) {
      toast.error(error.message || "The payment could not be voided.");
    }
  };

  const handlePrintPaymentsReport = () => {
    const reportWindow = window.open("", "_blank");
    if (!reportWindow) {
      toast.error("The print window was blocked. Please allow pop-ups and try again.");
      return;
    }
    reportWindow.opener = null;
    reportWindow.document.write(buildPaymentReportHtml({
      firmName: brandObj.orgName,
      range: reportRange,
      payments: reportPayments,
      preparedAt: new Date(),
    }));
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
  };

  const handleViewPaymentDocument = async (invoiceItem) => {
    try {
      if (invoiceItem.source === "email") {
        if (!invoiceItem.documentUrl) throw new Error(invoiceItem.fileNote || "No document was attached to this email.");
        window.open(invoiceItem.documentUrl, "_blank", "noopener,noreferrer");
        return;
      }
      let documentData = invoiceItem.invoiceBase64;
      if (invoiceItem.source === "fee_slip") {
        const response = await fetch(`${DEADLINE_API_BASE}/api/fee-slips/${encodeURIComponent(invoiceItem.sourceId)}`, {
          cache: "no-store"
        });
        const payload = await response.json();
        if (!response.ok || payload.ok === false) throw new Error(payload.error || "Failed to load fee slip");
        documentData = payload.fee_slip?.file_data;
      }
      if (!documentData) throw new Error("Document data was not found");
      const link = document.createElement("a");
      link.href = documentData;
      link.download = invoiceItem.invoiceName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error.message || "Failed to open document.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-850 p-6 rounded-xl shadow-sm border border-slate-100 dark:border-slate-800">
          <div className="space-y-1">
            <h1 className="text-xl font-bold tracking-tight text-slate-800 dark:text-white flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-teal-700" />
              Fee Slips & Invoices
            </h1>
            <p className="text-xs text-slate-500">
              {isAdminMode
                ? `Counsel fee slips and invoices for ${brandObj.orgName}: what each clerk is owed, what has been paid, and what to send the firm.`
                : "Counsel fee slips and invoices: what each clerk is owed, what has been paid, and the paid summaries."}
            </p>
          </div>
          {isAdminMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className={`gap-2 text-xs text-white print:hidden ${brandObj.bgButton}`}>
                  <Plus className="h-4 w-4" /> Add <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onSelect={() => setAddForm("fee_slip")}>Fee slip</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAddForm("statement")}>Clerk statement</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAddForm("paid_summary")}>Paid summary</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setActiveTab("recover")}>Invoice to recover from aid</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* SUMMARY: each figure opens the part of the page it comes from. */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 print:hidden">
          <button
            type="button"
            onClick={() => setActiveTab("invoices")}
            className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-slate-300"
          >
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">Outstanding</span>
            <span className="mt-1 block text-lg font-bold text-slate-900">{formatAud(summary.outstanding.total)}</span>
            <span className="block text-[11px] text-slate-500">{summary.outstanding.count} not yet paid</span>
          </button>
          <button
            type="button"
            disabled={!summary.toSort.count}
            onClick={() => { setActiveTab("invoices"); setSelectedClerkName(TO_SORT_GROUP); }}
            className={`rounded-xl border p-4 text-left shadow-sm ${summary.toSort.count ? "border-amber-300 bg-amber-50 hover:border-amber-400" : "border-slate-200 bg-white"}`}
          >
            <span className={`block text-[10px] font-bold uppercase tracking-wide ${summary.toSort.count ? "text-amber-800" : "text-slate-500"}`}>To sort</span>
            <span className="mt-1 block text-lg font-bold text-slate-900">{summary.toSort.count}</span>
            <span className="block text-[11px] text-slate-500">{summary.toSort.count ? `emailed, no clerk yet · ${formatAud(summary.toSort.total)}` : "everything has a clerk"}</span>
          </button>
          <button
            type="button"
            disabled={!isAdminMode}
            onClick={() => setActiveTab("payments")}
            className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm enabled:hover:border-slate-300"
          >
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">Paid {summary.paidThisMonth.label}</span>
            <span className="mt-1 block text-lg font-bold text-slate-900">{formatAud(summary.paidThisMonth.total)}</span>
            <span className="block text-[11px] text-slate-500">{summary.paidThisMonth.count} payment{summary.paidThisMonth.count === 1 ? "" : "s"} recorded</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("recover")}
            className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-slate-300"
          >
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">Still to recover from aid</span>
            <span className="mt-1 block text-lg font-bold text-slate-900">{formatAud(summary.toRecover.total)}</span>
            <span className="block text-[11px] text-slate-500">{summary.toRecover.count ? `${summary.toRecover.count} invoice${summary.toRecover.count === 1 ? "" : "s"}` : "nothing being tracked"}</span>
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="print:hidden">
            <TabsTrigger value="invoices" className="text-xs">Fee slips & invoices</TabsTrigger>
            <TabsTrigger value="summaries" className="text-xs">Paid summaries</TabsTrigger>
            {isAdminMode && <TabsTrigger value="payments" className="text-xs">Payments made</TabsTrigger>}
            <TabsTrigger value="recover" className="text-xs">
              Recover from aid{recoupSummary.outstandingCount ? ` (${recoupSummary.outstandingCount})` : ""}
            </TabsTrigger>
            <TabsTrigger value="claims" className="text-xs">
              Claim receipts{claimReceipts.data.receipts.some((receipt) => !receipt.links.length)
                ? ` (${claimReceipts.data.receipts.filter((receipt) => !receipt.links.length).length} to match)`
                : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="mt-0">
            <Card className="shadow-sm border-slate-205">
              <CardHeader className="py-4 border-b border-slate-100">
                <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Counsel Invoices Submitted for Payment
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Individual fee slips from Deadline Guard and counsel invoices share the same payment status and notes view.
                </CardDescription>
              </CardHeader>
              {/* Clerk is the first choice. Each list gets its own header with its own
                  total, and only the chosen one is shown - the screen exists to send a
                  clerk their outstanding fees, so a mixed list of every clerk is not it. */}
              <div className="border-b border-slate-100 bg-white px-4 py-3">
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
                  <Input
                    type="search"
                    value={feeSlipSearch}
                    onChange={(event) => setFeeSlipSearch(event.target.value)}
                    placeholder="Search fee slips: client, barrister, invoice number, amount…"
                    aria-label="Search fee slips"
                    className="h-9 pl-8 text-xs"
                  />
                </div>
                {searchingFeeSlips && (
                  <p className="mb-2 text-[11px] text-slate-500" role="status">
                    {filteredCounselInvoices.length === 0
                      ? "No fee slips match"
                      : filteredCounselInvoices.length === 1
                        ? "1 fee slip matches"
                        : `${filteredCounselInvoices.length} fee slips match`}
                    {selectedClerkName ? ` in ${selectedClerkName}` : " across all clerks"}
                    {statusFilters.length < PAYMENT_STATUS_VALUES.length ? " (in the selected status groups)" : ""}
                    {removedMatches.length ? `, and ${removedMatches.length} deleted (listed below the results)` : ""}.
                    {" "}
                    <button type="button" onClick={() => setFeeSlipSearch("")} className="font-semibold text-slate-700 underline">
                      Clear search
                    </button>
                  </p>
                )}
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    Clerk / list {clerkGroups.length > 0 && `(${clerkGroups.length})`}
                  </span>
                  {selectedClerkGroup && (
                    <Button
                      variant="ghost"
                      onClick={() => setSelectedClerkName("")}
                      className="h-7 text-[11px] text-slate-500"
                    >
                      Show all clerks
                    </Button>
                  )}
                </div>
                {clerkGroups.length === 0 ? (
                  <p className="text-xs text-slate-450">No fee slips for the current selections.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {clerkGroups.map((group) => {
                      const active = group.clerk === selectedClerkName;
                      return (
                        <button
                          key={group.clerk}
                          type="button"
                          onClick={() => setSelectedClerkName(active ? "" : group.clerk)}
                          aria-pressed={active}
                          className={`rounded-lg border px-3 py-2 text-left transition ${
                            active
                              ? "border-emerald-300 bg-emerald-50 shadow-sm"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <span className="block text-xs font-bold text-slate-800">{group.clerk}</span>
                          <span className="block text-[10px] font-semibold text-slate-500">
                            {clerkTotals.get(group.clerk)?.unpaid ?? 0} unpaid
                            {" · "}
                            {formatAud(clerkTotals.get(group.clerk)?.owing)}
                            {clerkTotals.get(group.clerk)?.paid > 0 && ` · ${clerkTotals.get(group.clerk).paid} paid`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {cover.count > 0 && (() => {
                  const chosen = selectedClerkName ? cover.byClerk.get(selectedClerkName) : null;
                  const parts = chosen
                    ? [
                        chosen.on_statement.count && `${chosen.on_statement.count} already on the statement`,
                        chosen.before_statement.count && `${chosen.before_statement.count} dated on or before it and not on it, so treated as paid`,
                        chosen.same_invoice.count && `${chosen.same_invoice.count} the same invoice as another item already listed`,
                      ].filter(Boolean)
                    : [];
                  return (
                    <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" role="status">
                      {chosen
                        ? <>{showCovered ? "Showing" : "Not counted"}: {parts.join("; ")}{chosen.asOf ? <> ({selectedClerkName}&apos;s statement of {auDay(chosen.asOf)})</> : null}. </>
                        : <>{showCovered ? "Showing" : "Not counted"}: {cover.count} item{cover.count === 1 ? "" : "s"} already in a clerk&apos;s list or covered by a clerk&apos;s statement. </>}
                      <button type="button" onClick={() => setShowCovered((value) => !value)} className="font-semibold text-slate-800 underline">
                        {showCovered ? "Hide them" : "Show them"}
                      </button>
                    </p>
                  );
                })()}
                {archivedError ? (
                  <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" role="status">
                    Archived paid lines could not be loaded, so every paid line is listed.
                  </p>
                ) : archivedHere > 0 && (
                  <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" role="status">
                    {showArchived
                      ? <>Showing {archivedHere} archived paid line{archivedHere === 1 ? "" : "s"}{selectedClerkName ? ` for ${selectedClerkName}` : ""}. </>
                      : <>{archivedHere} paid line{archivedHere === 1 ? "" : "s"} archived{selectedClerkName ? ` for ${selectedClerkName}` : ""}: kept, but not listed or counted. A search still finds them. </>}
                    <button type="button" onClick={() => setShowArchived((value) => !value)} className="font-semibold text-slate-800 underline">
                      {showArchived ? "Hide them" : "Show them"}
                    </button>
                  </p>
                )}
              </div>
              {/* Controls sit in their own strip below the header. The header itself is left
                  exactly as it was - an earlier attempt rebuilt it into a two-column row and
                  that display change was rejected. */}
              <div className="flex flex-col gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                <details className="min-w-[260px] rounded-lg border border-slate-200 bg-white text-xs shadow-sm">
                  <summary className="cursor-pointer px-3 py-2 font-semibold text-slate-700">
                    Status groups ({statusFilters.length} selected)
                  </summary>
                  <div className="grid gap-2 border-t border-slate-100 p-3 sm:grid-cols-2">
                    {PAYMENT_STATUSES.map(option => (
                      <label key={option.value} className="flex items-center gap-2 text-[11px] font-medium text-slate-600">
                        <Checkbox
                          checked={statusFilters.includes(option.value)}
                          onCheckedChange={() => toggleStatusFilter(option.value)}
                          aria-label={`Include ${option.label}`}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </details>
                <div className="flex flex-wrap items-center gap-2">
                  {showsReimbursement && reimbursementStatements.length > 0 && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      {reimbursementStatements.length} marked {REIMBURSEMENT_LABEL}
                    </span>
                  )}
                  {showsReimbursement && reimbursementSaveError && (
                    <span className="rounded border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
                      Last reimbursement mark not saved
                    </span>
                  )}
                  {backsheetError && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      Counsel invoices could not be loaded
                    </span>
                  )}
                  {clerkStatementsError && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      Clerk fee slips could not be loaded
                    </span>
                  )}
                  {emailIntakeError && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      Emailed fee slips could not be loaded
                    </span>
                  )}
                  {showsReimbursement && reimbursementLoadError && counselInvoices.length > 0 && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      Reimbursement marks could not be loaded
                    </span>
                  )}
                  <Select value={feeSlipSort} onValueChange={setFeeSlipSort}>
                    <SelectTrigger className="h-9 w-[210px] bg-white text-xs" aria-label="Sort fee slips by">
                      <span className="mr-1 text-slate-500">Sort:</span>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_SLIP_SORTS.map(option => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={printLayout} onValueChange={setPrintLayout}>
                    <SelectTrigger className="h-9 w-[180px] bg-white text-xs" aria-label="Print layout">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRINT_LAYOUTS
                        .filter(option => showsReimbursement || option.value !== "reimbursement")
                        .map(option => (
                        <SelectItem key={option.value} value={option.value} className="text-xs">
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handlePrintSelection} variant="outline" className="h-9 gap-2 text-xs">
                    <Printer className="h-4 w-4" />
                    Print selection
                  </Button>
                </div>
              </div>
              <CardContent className="p-0">
                {payCounselRows.length > 0 && (
                  <div role="alert" className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <p className="flex items-center gap-2 font-semibold">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      {payCounselRows.length} fee slip{payCounselRows.length === 1 ? "" : "s"} paid to the firm - pay counsel
                    </p>
                    <ul className="mt-1 space-y-0.5 pl-6 text-xs">
                      {payCounselRows.map((row) => (
                        <li key={row.id}>
                          <button type="button" onClick={() => setSelectedClerkName(row.clerkName)} className="text-left hover:underline">
                            {row.clientName} - {row.counselName} ({row.clerkName}) {Number.parseFloat(row.invoiceAmount) ? `$${Number.parseFloat(row.invoiceAmount).toFixed(2)}` : ""}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {printedPaid && canArchive && (() => {
                  const ready = archivableRows(
                    counselInvoices.filter((row) => printedPaid.ids.has(row.id)),
                    { archived: archivedIds, edits: paymentEdits },
                  );
                  if (!ready.length) return null;
                  const lines = `${ready.length} paid line${ready.length === 1 ? "" : "s"}`;
                  return (
                    <div role="status" className="flex flex-wrap items-center gap-2 border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-900">
                      <span className="font-semibold">
                        Statement printed? Archive its {lines}{printedPaid.clerk ? ` for ${printedPaid.clerk}` : ""} now.
                      </span>
                      <span className="text-[11px] text-emerald-800">
                        They stop being listed and counted, and are kept: a search still finds them, and they can be brought back.
                      </span>
                      <Button
                        type="button"
                        onClick={() => handleArchivePaid(ready)}
                        disabled={archiving}
                        className="h-7 bg-emerald-700 px-3 text-[11px] text-white hover:bg-emerald-800"
                      >
                        {archiving ? "Archiving..." : `Archive ${lines}`}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setPrintedPaid(null)} className="h-7 px-2 text-[11px]">
                        Not now
                      </Button>
                    </div>
                  );
                })()}
                {sortingCheckType && sortRows.length > 0 && (
                  <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2">
                    <label className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                      <Checkbox
                        checked={allSortTicked}
                        onCheckedChange={() => setSortTicked(allSortTicked ? new Set() : new Set(sortRows.map((row) => row.id)))}
                        aria-label="Select all emailed items to sort"
                      />
                      Select all ({sortRows.length})
                    </label>
                    <span className="text-[11px] text-amber-800">{sortChosen.length} selected</span>
                    <Button
                      type="button"
                      onClick={() => bulkSetKind("fee_slip")}
                      disabled={!sortChosen.length || Boolean(bulkSorting)}
                      className="h-7 bg-violet-700 px-3 text-[11px] text-white hover:bg-violet-800"
                    >
                      Mark as fee slips
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => bulkSetKind("disbursement")}
                      disabled={!sortChosen.length || Boolean(bulkSorting)}
                      className="h-7 px-3 text-[11px]"
                    >
                      Mark as disbursements
                    </Button>
                    {bulkSorting && <span className="text-[11px] text-amber-900" role="status">{bulkSorting}</span>}
                  </div>
                )}
                {!selectedClerkName && !searchingFeeSlips ? (
                  <div className="p-12 text-center text-xs font-semibold text-slate-450">
                    Choose a clerk or list above to see their fee slips by status. Or search for one above.
                  </div>
                ) : filteredCounselInvoices.length === 0 ? (
                  <div className="p-12 text-center text-slate-450 text-xs font-semibold">
                    {searchingFeeSlips
                      ? `No fee slips match "${feeSlipSearch.trim()}"${selectedClerkName ? ` in ${selectedClerkName}` : ""}.`
                      : `Nothing for ${selectedClerkName} in the selected status groups.`}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    <div className="sticky top-0 z-10 bg-white px-4 py-2 dark:bg-slate-900">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {unsavedElsewhere > 0 && (
                        <span className="text-[11px] font-medium text-amber-700" role="status">
                          {unsavedElsewhere} unsaved in other lists
                        </span>
                      )}
                      <Button
                        type="button"
                        onClick={() => handleSaveAll()}
                        disabled={savingAll || unsavedHere === 0}
                        className="h-8 gap-1 bg-emerald-600 px-3 text-[11px] font-medium text-white hover:bg-emerald-700"
                      >
                        {savingAll ? "Saving..." : `Save all${unsavedHere ? ` (${unsavedHere})` : ""}`}
                      </Button>
                    </div>
                    {linesNeedingType > 0 && (
                      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 rounded-md border border-amber-200 bg-amber-50 p-2" role="status">
                        <span className="text-[11px] font-medium text-amber-900">
                          {linesNeedingType} line{linesNeedingType === 1 ? " has" : "s have"} no type yet, so the payment can't be recorded. Set any that differ on the line, then save the rest as:
                        </span>
                        {PAYMENT_TYPES.map(option => (
                          <Button
                            key={option.value}
                            type="button"
                            variant="outline"
                            onClick={() => handleSaveAll(option.value)}
                            className="h-7 bg-white px-2 text-[11px] font-semibold"
                          >
                            {option.label}
                          </Button>
                        ))}
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setAskSaveAllType(false)}
                          className="h-7 px-2 text-[11px]"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    </div>
                    {groupedCounselInvoices.flatMap((group) => [
                      <div
                        key={`group-${group.status}`}
                        className="flex items-center justify-between gap-3 bg-slate-100/80 px-4 py-2 dark:bg-slate-800/60"
                      >
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-700">
                          {group.label}
                        </span>
                        <span className="flex flex-wrap items-center justify-end gap-2 text-[10px] font-semibold text-slate-500">
                          {canEmailFinance && group.status === SEND_TO_FINANCE && (
                            <>
                              <button
                                type="button"
                                onClick={() => setFinanceTicked((current) => {
                                  const all = group.items.every((item) => current.has(item.id));
                                  const next = new Set(current);
                                  for (const item of group.items) {
                                    if (all) next.delete(item.id);
                                    else next.add(item.id);
                                  }
                                  return next;
                                })}
                                className="font-semibold text-slate-700 underline"
                              >
                                {group.items.every((item) => financeTicked.has(item.id)) ? "Clear all" : "Select all"}
                              </button>
                              <Button
                                type="button"
                                onClick={() => setFinanceEmailOpen(true)}
                                disabled={!financeRows.length}
                                className="h-7 gap-1 bg-sky-700 px-2 text-[10px] font-bold uppercase tracking-wide text-white hover:bg-sky-800"
                              >
                                Email finance ({financeRows.length})
                              </Button>
                            </>
                          )}
                          {group.status === "paid" && canArchive && (() => {
                            const ready = archivableRows(group.items, { archived: archivedIds, edits: paymentEdits });
                            return ready.length > 0 && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleArchivePaid(ready, { ask: true })}
                                disabled={archiving}
                                title="Once the statement is printed: take these out of the list and its totals. Nothing is deleted."
                                className="h-7 bg-white px-2 text-[10px] font-bold uppercase tracking-wide"
                              >
                                {archiving ? "Archiving..." : `Archive paid (${ready.length})`}
                              </Button>
                            );
                          })()}
                          <span>
                            {group.items.length} fee slip{group.items.length === 1 ? "" : "s"}
                            {" · "}
                            {group.status === "paid"
                              ? `${formatAud(paidLines(group.items, currentStatusOf).total)} paid`
                              : `${formatAud(unpaidTotal(group.items, { statusOf: currentStatusOf, payments }))}${group.status === "partial_paid" ? " still owing" : ""}`}
                          </span>
                        </span>
                      </div>,
                      ...(canEmailFinance && group.status === SEND_TO_FINANCE && financeEmailOpen && financeRows.length
                        ? [
                          <FinanceFeeSlipEmail
                            key="finance-email"
                            rows={financeRows}
                            signOff={emailSignOff}
                            loadAttachments={loadFeeSlipAttachments}
                            onSent={handleFinanceSent}
                            onClose={() => setFinanceEmailOpen(false)}
                          />,
                        ]
                        : []),
                      ...group.items.map((inv) => (

                      <div key={inv.id} data-focus-id={inv.id} className="p-4 hover:bg-slate-50/50 transition">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            {sortingCheckType && inv.source === "email" && inv.emailKind === "check_type" && (
                              <Checkbox
                                checked={sortTicked.has(inv.id)}
                                onCheckedChange={() => setSortTicked((current) => {
                                  const next = new Set(current);
                                  if (next.has(inv.id)) next.delete(inv.id);
                                  else next.add(inv.id);
                                  return next;
                                })}
                                aria-label={`Select ${inv.clientName} to sort`}
                              />
                            )}
                            {canEmailFinance && currentStatusOf(inv) === SEND_TO_FINANCE && (
                              <Checkbox
                                checked={financeTicked.has(inv.id)}
                                onCheckedChange={() => toggleFinanceTick(inv.id)}
                                aria-label={`Include ${inv.clientName} in the email to finance`}
                              />
                            )}
                            <span className="text-xs font-bold text-slate-800">
                              {inv.counselName} ({inv.clerkName})
                            </span>
                            <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${
                              normalizePaymentStatus(paymentEdits[inv.id]?.status || inv.status) === "paid"
                                ? "bg-green-50 text-green-700 border border-green-100"
                                : normalizePaymentStatus(paymentEdits[inv.id]?.status || inv.status) === "partial_paid"
                                  ? "bg-blue-50 text-blue-700 border border-blue-100"
                                  : "bg-amber-50 text-amber-700 border border-amber-100"
                            }`}>
                              {paymentStatusLabel(paymentEdits[inv.id]?.status || inv.status)}
                            </span>
                            {inv.source === "clerk_statement" && (
                              <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-sky-50 text-sky-700 border border-sky-100">
                                Clerk Statement
                              </span>
                            )}
                            {isArchived(inv, archivedIds, currentStatusOf) && (
                              <>
                                <span
                                  className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200"
                                  title={`Archived ${new Date(archivedIds.get(inv.id)?.archived_at || "").toLocaleDateString("en-AU")}${archivedIds.get(inv.id)?.archived_by ? ` by ${archivedIds.get(inv.id).archived_by}` : ""}`}
                                >
                                  Archived
                                </span>
                                {canArchive && (
                                  <button
                                    type="button"
                                    onClick={(event) => { event.stopPropagation(); handleUnarchive(inv); }}
                                    className="text-[10px] font-semibold text-slate-600 underline"
                                  >
                                    Bring back
                                  </button>
                                )}
                              </>
                            )}
                            {inv.amountSuspect && (
                              <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-red-50 text-red-700 border border-red-200">
                                Amount unreadable
                              </span>
                            )}
                            {inv.source === "fee_slip" && (
                              <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-violet-50 text-violet-700 border border-violet-100">
                                Fee Slip
                              </span>
                            )}
                            {inv.source === "email" && (
                              <>
                                <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase border ${
                                  inv.emailKind === "fee_slip"
                                    ? "bg-violet-50 text-violet-700 border-violet-100"
                                    : inv.emailKind === "disbursement"
                                      ? "bg-orange-50 text-orange-700 border-orange-200"
                                      : "bg-red-50 text-red-700 border-red-200"
                                }`}>
                                  {EMAIL_KIND_LABELS[inv.emailKind] || EMAIL_KIND_LABELS.check_type}
                                </span>
                                <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-slate-100 text-slate-600 border border-slate-200">
                                  From email
                                </span>
                                {inv.amountFromPdf && !paymentEdits[inv.id]?.amount && (
                                  <span
                                    className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-amber-50 text-amber-800 border border-amber-200"
                                    title="Read off the attached PDF. Check it against the invoice; typing an amount replaces it."
                                  >
                                    Amount from PDF
                                  </span>
                                )}
                                {CLERK_FROM_NOTES[inv.clerkFrom] && (
                                  <span
                                    className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-teal-50 text-teal-700 border border-teal-100"
                                    title={CLERK_FROM_NOTES[inv.clerkFrom].title}
                                  >
                                    {CLERK_FROM_NOTES[inv.clerkFrom].label}
                                  </span>
                                )}
                                {inv.possibleDuplicate && (
                                  <span
                                    className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300"
                                    title={`Invoice ${inv.invoiceRef} was already filed from another email`}
                                  >
                                    Possible duplicate
                                  </span>
                                )}
                              </>
                            )}
                            {claimReceiptsFor(inv.id).map((receipt) => (
                              <a
                                key={receipt.id}
                                href={statementFileUrl({ fileUrl: receipt.fileUrl }) || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                                title={`ATLAS claim ${receipt.claimId || ""} ${receipt.claimRef ? `(${receipt.claimRef})` : ""}`.trim()}
                              >
                                Claim receipt
                              </a>
                            ))}
                            {showsReimbursement && isReimbursed(inv.id) && (
                              <span className="text-[9px] px-2 py-0.5 rounded font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200">
                                {REIMBURSEMENT_LABEL}
                              </span>
                            )}
                            {/* Closed, the line still has to say which fee slip it is and what
                                it is worth, or the list cannot be read down. */}
                            {!isRowOpen(inv.id) && (
                              <span className="truncate text-[10px] text-slate-500">
                                {inv.clientName} &middot; ${parseFloat(inv.invoiceAmount).toFixed(2)}
                                {rowSaveNote(inv) && (
                                  <span className={`font-semibold ${rowSaveNote(inv).tone}`}> &middot; {rowSaveNote(inv).short}</span>
                                )}
                              </span>
                            )}
                            {/* The invoice itself, without opening the line. */}
                            {!isRowOpen(inv.id) && inv.source === "clerk_statement" && attachmentsFor(inv)[0] && (
                              <a
                                href={attachmentsFor(inv)[0].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={attachmentsFor(inv)[0].filename}
                                className="inline-flex shrink-0 items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
                              >
                                <Download className="h-3 w-3" /> View invoice
                              </a>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                          {canArchive && (
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleDeleteLine(inv)}
                              aria-label={`Delete the line for ${inv.clientName}`}
                              title="Delete this line. It is no longer listed or counted; search for it to restore it."
                              className="h-7 w-7 p-0 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => toggleRow(inv.id)}
                            aria-expanded={isRowOpen(inv.id)}
                            aria-controls={`fee-slip-row-${inv.id}`}
                            className="h-7 shrink-0 gap-1 px-2 text-[10px] font-bold uppercase tracking-wide text-slate-600"
                          >
                            {isRowOpen(inv.id)
                              ? <><ChevronUp className="h-3.5 w-3.5" />Collapse</>
                              : <><ChevronDown className="h-3.5 w-3.5" />Expand</>}
                          </Button>
                          </div>
                        </div>
                        {isRowOpen(inv.id) && (
                        <div id={`fee-slip-row-${inv.id}`} className="mt-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.8fr)] gap-4">
                        <div className="space-y-1.5 min-w-0 flex-1">
                          <div className="text-[10px] text-slate-500 space-y-0.5">
                            <p><span className="font-semibold text-slate-700">Client:</span> {inv.clientName}</p>
                            <p><span className="font-semibold text-slate-700">Invoice:</span> {inv.invoiceName} ({(inv.invoiceSize / 1024).toFixed(1)} KB)</p>
                            <p><span className="font-semibold text-slate-700">Fee Amount:</span> ${parseFloat(inv.invoiceAmount).toFixed(2)}</p>
                            <p><span className="font-semibold text-slate-700">Submitted:</span> {inv.uploadedAt.split("T")[0]}</p>
                            {inv.matterId && <p><span className="font-semibold text-slate-700">Matter:</span> {inv.matterId}</p>}
                            {inv.source === "email" && (
                              <>
                                <p><span className="font-semibold text-slate-700">Email:</span> {inv.subject || "(no subject)"}</p>
                                <p><span className="font-semibold text-slate-700">From:</span> {inv.rawEmail.sender || "Unknown sender"}</p>
                                {!inv.tagged && (
                                  <p className="text-amber-700">Not tagged - filed because the attachment looks like something to pay.</p>
                                )}
                                {inv.fileNote && <p className="text-red-700">{inv.fileNote}</p>}
                                {inv.invoiceRef && (
                                  <p><span className="font-semibold text-slate-700">Invoice no:</span> {inv.invoiceRef}</p>
                                )}
                                {inv.possibleDuplicate && (
                                  <p className="text-amber-700">
                                    This invoice number was already filed from another email. Check it is not the same bill before paying.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                          {inv.source === "clerk_statement" && (
                            <div className="pt-1">
                              {attachmentsFor(inv).length > 0 ? (
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-[10px] font-semibold text-slate-700">Matching invoice:</span>
                                  {attachmentsFor(inv).map((attachment) => (
                                    <a
                                      key={`${inv.id}-${attachment.filename}`}
                                      href={attachment.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={attachment.meta}
                                      className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
                                    >
                                      <Download className="h-3 w-3" />
                                      {attachment.filename}
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400">
                                  {matchesLoading ? "Looking for the invoice…" : "No matching invoice found"}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                          <div>
                            <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              Payment / action status
                            </Label>
                            <Select
                              value={currentStatusOf(inv)}
                              onValueChange={status => {
                                setPaymentEdits(current => ({
                                  ...current,
                                  [inv.id]: { ...current[inv.id], status }
                                }));
                                // The line moves to its new status group; it closes there
                                // unless that status needs more from it (src/lib/feeSlipRows.js).
                                if (!staysOpenAfterStatus(status, { isAdminMode })) {
                                  setOpenRowIds(current => current.filter(value => value !== inv.id));
                                }
                              }}
                            >
                              <SelectTrigger className="h-9 bg-white text-xs" aria-label={`Payment status for ${inv.clientName}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {/* A record still on a retired status needs an entry here, or the
                                    trigger would render blank and the real status would be
                                    invisible. It is disabled: readable, but not re-selectable
                                    once the invoice has been moved off it. */}
                                {isLegacyPaymentStatus(currentStatusOf(inv)) && (
                                  <SelectItem value={currentStatusOf(inv)} disabled className="text-xs">
                                    {paymentStatusLabel(currentStatusOf(inv))}
                                  </SelectItem>
                                )}
                                {PAYMENT_STATUSES.map(option => (
                                  <SelectItem key={option.value} value={option.value} className="text-xs">
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {inv.source === "email" && isAdminMode && (
                            <div className="space-y-2 rounded-md border border-slate-200 bg-white p-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Type</Label>
                                  <Select
                                    value={inv.emailKind}
                                    onValueChange={kind => handleEmailItemChange(inv, { kind }, kind === "disbursement"
                                      ? "Moved to the Disbursements page."
                                      : `Marked as ${EMAIL_KIND_LABELS[kind]}.`)}
                                  >
                                    <SelectTrigger className="h-8 bg-white text-xs" aria-label={`Type of emailed item for ${inv.clientName}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="check_type" disabled className="text-xs">Check type</SelectItem>
                                      <SelectItem value="fee_slip" className="text-xs">Fee slip</SelectItem>
                                      <SelectItem value="disbursement" className="text-xs">Disbursement</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {inv.emailKind === "fee_slip" && (
                                  <div>
                                    <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Clerk / list</Label>
                                    <Select
                                      value={inv.rawEmail.clerk_name || undefined}
                                      onValueChange={clerk => handleEmailItemChange(inv, { clerk_name: clerk }, `Moved to ${clerk}.`)}
                                    >
                                      <SelectTrigger className="h-8 bg-white text-xs" aria-label={`Clerk for ${inv.clientName}`}>
                                        <SelectValue placeholder="Choose clerk" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {clerkChoices.map(clerk => (
                                          <SelectItem key={clerk} value={clerk} className="text-xs">{clerk}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  value={paymentEdits[inv.id]?.clientName ?? inv.rawEmail.client_name ?? ""}
                                  onChange={event => setPaymentEdits(current => ({
                                    ...current,
                                    [inv.id]: { ...current[inv.id], clientName: event.target.value }
                                  }))}
                                  placeholder="Client"
                                  aria-label={`Client for emailed item ${inv.invoiceName}`}
                                  className="h-8 bg-white text-xs"
                                />
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={paymentEdits[inv.id]?.amount ?? inv.rawEmail.amount ?? ""}
                                  onChange={event => setPaymentEdits(current => ({
                                    ...current,
                                    [inv.id]: { ...current[inv.id], amount: event.target.value }
                                  }))}
                                  placeholder="Amount"
                                  aria-label={`Amount for emailed item ${inv.invoiceName}`}
                                  className="h-8 bg-white text-xs"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={() => handleDismissEmailItem(inv)}
                                className="h-7 px-2 text-[10px] font-bold uppercase tracking-wide text-rose-600 hover:bg-rose-50"
                              >
                                Dismiss
                              </Button>
                            </div>
                          )}
                          {isAdminMode && ["paid", "partial_paid"].includes(normalizePaymentStatus(currentStatusOf(inv))) && (
                            <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800">Record payment</span>
                                <span className="text-[10px] text-slate-600">Still owing {formatMoney(amountOwing(inv, payments))}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Paid on</Label>
                                  <Input
                                    type="date"
                                    value={paymentEdits[inv.id]?.paidOn ?? todayIso()}
                                    onChange={event => setPaymentEdits(current => ({
                                      ...current,
                                      [inv.id]: { ...current[inv.id], paidOn: event.target.value }
                                    }))}
                                    aria-label={`Date paid for ${inv.clientName}`}
                                    className="h-8 bg-white text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Amount paid</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={paymentEdits[inv.id]?.amountPaid
                                      ?? (normalizePaymentStatus(currentStatusOf(inv)) === "paid" ? String(amountOwing(inv, payments)) : "")}
                                    onChange={event => setPaymentEdits(current => ({
                                      ...current,
                                      [inv.id]: { ...current[inv.id], amountPaid: event.target.value }
                                    }))}
                                    placeholder="0.00"
                                    aria-label={`Amount paid for ${inv.clientName}`}
                                    className="h-8 bg-white text-xs"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Type</Label>
                                  <Select
                                    value={paymentEdits[inv.id]?.paymentType || defaultPaymentType(inv) || undefined}
                                    onValueChange={paymentType => setPaymentEdits(current => ({
                                      ...current,
                                      [inv.id]: { ...current[inv.id], paymentType }
                                    }))}
                                  >
                                    <SelectTrigger className="h-8 bg-white text-xs" aria-label={`Payment type for ${inv.clientName}`}>
                                      <SelectValue placeholder="Choose type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {PAYMENT_TYPES.map(option => (
                                        <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">Sent by</Label>
                                  <Input
                                    value={paymentEdits[inv.id]?.sentBy ?? defaultSentBy(inv)}
                                    onChange={event => setPaymentEdits(current => ({
                                      ...current,
                                      [inv.id]: { ...current[inv.id], sentBy: event.target.value }
                                    }))}
                                    placeholder="Who sent it to you"
                                    aria-label={`Who sent the invoice for ${inv.clientName}`}
                                    className="h-8 bg-white text-xs"
                                  />
                                </div>
                              </div>
                              <p className="text-[10px] text-slate-500">
                                Saved with the status below, and listed on the payments report.
                              </p>
                            </div>
                          )}
                          {normalizePaymentStatus(currentStatusOf(inv)) === "partial_paid" && (isAdminMode ? (
                            <PartialPaidSplit
                              idPrefix={`fee-split-${inv.id}`}
                              total={inv.invoiceAmount}
                              vlaClaimed={partialSplits.data.splits[inv.id]?.vlaClaimed}
                              fromBudget={partialSplits.data.splits[inv.id]?.fromBudget}
                              disabled={!partialSplits.loaded || partialSplits.loadError || partialSplits.saving}
                              onCommit={(fields) => savePartialSplit(inv, fields)}
                            />
                          ) : partialSplits.data.splits[inv.id] && (() => {
                            const saved = partialSplits.data.splits[inv.id];
                            const split = partialSplit(inv.invoiceAmount, saved.vlaClaimed, saved.fromBudget);
                            return (
                              <p className="rounded-md border border-violet-200 bg-violet-50/60 p-2 text-[10px] text-violet-800">
                                Partial paid: {formatMoney(split.vlaClaimed ?? 0)} claimed from VLA, {formatMoney(split.fromBudget ?? 0)} to pay from budget.
                              </p>
                            );
                          })())}
                          {paymentsForRow(payments, inv.id).map(paid => (
                            <div
                              key={paid.id}
                              className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white p-2 text-[10px] text-slate-600"
                            >
                              <span>
                                Paid {formatMoney(paid.amount_paid)} on {formatDay(paid.paid_on)}
                                {" · "}{paymentTypeLabel(paid.payment_type)}
                                {paid.recorded_by ? ` · recorded by ${paid.recorded_by}` : ""}
                              </span>
                              {isAdminMode && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => handleVoidPayment(paid)}
                                  className="h-6 shrink-0 px-2 text-[10px] font-bold uppercase tracking-wide text-rose-600 hover:bg-rose-50"
                                >
                                  Void
                                </Button>
                              )}
                            </div>
                          ))}
                          <div className="rounded-md border border-slate-200 bg-white p-2">
                            <Label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              Recover from aid
                            </Label>
                            <Select
                              value={recoup.data.entries[inv.id]?.stage || "none"}
                              onValueChange={stage => setRecoupStage(inv, stage === "none" ? "" : stage)}
                              disabled={!recoup.loaded || recoup.loadError || recoup.saving}
                            >
                              <SelectTrigger className="h-8 text-xs" aria-label={`Recover from aid for ${inv.clientName}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" className="text-xs">Not paid from {brandObj.appPrefix} budget</SelectItem>
                                {recoupStageOptions.map(option => (
                                  <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {showsReimbursement && (
                            <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/70 p-2">
                              <Checkbox
                                checked={isReimbursed(inv.id)}
                                onCheckedChange={checked => setReimbursed(inv.id, checked === true)}
                                aria-label={`${REIMBURSEMENT_LABEL} for ${inv.clientName}`}
                                className="mt-0.5"
                              />
                              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800">
                                {REIMBURSEMENT_LABEL}
                              </span>
                            </label>
                          )}
                          {inv.statementNotes && (
                            <p className="rounded-md border border-slate-200 bg-white p-2 text-[10px] text-slate-600">
                              <span className="font-semibold text-slate-700">On the statement:</span>{" "}
                              {inv.statementNotes}
                            </p>
                          )}
                          <Textarea
                            value={paymentEdits[inv.id]?.notes ?? inv.notes ?? ""}
                            onChange={event => setPaymentEdits(current => ({
                              ...current,
                              [inv.id]: { ...current[inv.id], notes: event.target.value }
                            }))}
                            placeholder="Payment notes"
                            aria-label={`Payment notes for ${inv.clientName}`}
                            rows={2}
                            className="text-xs bg-white"
                          />
                          {/* Email Clerk / Email Instructor open an email to send, as they do in
                              Deadline Guard (src/lib/clerkEmail.js). Admin only: it sends from
                              the service's Gmail account. */}
                          {isAdminMode && isEmailStatus(currentStatusOf(inv)) && !closedEmails.has(`${inv.id}:${currentStatusOf(inv)}`) && (
                            <ClerkEmailComposer
                              key={`${inv.id}:${currentStatusOf(inv)}`}
                              row={inv}
                              status={currentStatusOf(inv)}
                              rows={counselInvoices}
                              signOff={emailSignOff}
                              onCancel={() => setClosedEmails(current => new Set(current).add(`${inv.id}:${currentStatusOf(inv)}`))}
                              // Sent: the line becomes Clerk / Instructor Emailed with the date in
                              // its notes, saves itself, and closes in its new group.
                              onSent={({ to }) => {
                                const sent = afterEmailSent({
                                  status: currentStatusOf(inv),
                                  notes: paymentEdits[inv.id]?.notes ?? inv.notes ?? "",
                                  to,
                                });
                                setPaymentEdits(current => ({
                                  ...current,
                                  [inv.id]: { ...current[inv.id], ...sent }
                                }));
                                setOpenRowIds(current => current.filter(value => value !== inv.id));
                              }}
                            />
                          )}
                          {isAdminMode && isEmailStatus(currentStatusOf(inv)) && closedEmails.has(`${inv.id}:${currentStatusOf(inv)}`) && (
                            <button
                              type="button"
                              onClick={() => setClosedEmails(current => {
                                const next = new Set(current);
                                next.delete(`${inv.id}:${currentStatusOf(inv)}`);
                                return next;
                              })}
                              className="text-[11px] font-semibold text-sky-800 underline"
                            >
                              Write the email
                            </button>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              onClick={() => handleViewPaymentDocument(inv)}
                              variant="outline"
                              className="h-8 text-[11px] gap-1 px-3 border-slate-200"
                            >
                              <Download className="w-3.5 h-3.5" />
                              View {inv.source === "fee_slip" ? "Fee Slip" : inv.source === "email" ? "Document" : "Invoice"}
                            </Button>
                            <Button
                              onClick={() => handleUpdatePayment(inv)}
                              disabled={rowSaveState[String(inv.id)]?.state === "saving"}
                              className="h-8 text-[11px] gap-1 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                            >
                              {rowSaveState[String(inv.id)]?.state === "saving" ? "Saving..." : "Save status & notes"}
                            </Button>
                            {rowSaveNote(inv) && (
                              <span className={`text-[11px] font-medium ${rowSaveNote(inv).tone}`} role="status">
                                {rowSaveNote(inv).text}
                              </span>
                            )}
                          </div>
                        </div>
                        </div>
                        )}
                      </div>
                      )),
                    ])}
                    {(selectedClerkGroup || searchingFeeSlips) && filteredCounselInvoices.length > 0 && (
                      <div className="flex items-center justify-between gap-3 bg-slate-800 px-4 py-3 text-white">
                        <span className="text-[11px] font-bold uppercase tracking-wide">
                          {searchingFeeSlips && !selectedClerkName ? "Search results" : selectedClerkName} — total outstanding
                        </span>
                        <span className="text-sm font-bold">
                          {formatAud(unpaidTotal(filteredCounselInvoices, { statusOf: currentStatusOf, payments }))}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <DismissedMatches rows={removedMatches} onRestore={handleRestoreLine} restoringId={restoringId} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="summaries" className="mt-0">
            <Card className="shadow-sm border-slate-150">
              
              {/* Header with Search and Filter tools */}
              <CardHeader className="py-4 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 rounded-t-lg">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-700">Paid Summaries Repository</CardTitle>
                  <CardDescription className="text-slate-400 text-xs">Search and download published paid summaries</CardDescription>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative w-full md:w-44">
                    <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <Input
                      type="search"
                      placeholder="Search PDF..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-8 h-8 text-[11px]"
                    />
                  </div>

                  <div className="w-full md:w-36">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="h-8 text-[11px] px-2">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">All Types</SelectItem>
                        <SelectItem value="clerk_fee_slip" className="text-xs">Fee Slips</SelectItem>
                        <SelectItem value="invoice_payment" className="text-xs">Invoices</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-full md:w-36">
                    <Select value={clerkFilter} onValueChange={setClerkFilter}>
                      <SelectTrigger className="h-8 text-[11px] px-2">
                        <SelectValue placeholder="All Lists" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all" className="text-xs">All Lists</SelectItem>
                        {PAID_SUMMARY_CLERKS.map((c) => (
                          <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>

              {/* PDF summaries list */}
              <CardContent className="p-0">
                {filteredStatements.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-xs font-semibold">
                    No paid summaries published yet for the current selections.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[500px] overflow-y-auto">
                    {filteredStatements.map((d) => {
                      const fd = d.form_data;
                      const isClerk = fd.type === "clerk_fee_slip";
                      
                      return (
                        <div key={d.id} className="p-4 hover:bg-slate-50/50 transition flex flex-col gap-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="min-w-0 flex-1 space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase ${
                                  isClerk ? "bg-teal-50 text-teal-700 border border-teal-100" : "bg-blue-50 text-blue-700 border border-blue-100"
                                }`}>
                                  {isClerk ? "Clerk Summary" : "Invoice Paid"}
                                </span>
                                <span className="text-xs font-bold text-slate-800 truncate">
                                  {clerkDisplayName(fd.clerkName)} ({fd.statementMonth} {fd.statementYear})
                                </span>
                              </div>
                              
                              <div className="text-[10px] text-slate-500 space-y-0.5">
                                <p className="flex items-center gap-1.5 text-slate-450 font-medium">
                                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                                  <span>PDF: {fd.fileName} ({(fd.fileSize / 1024).toFixed(1)} KB)</span>
                                </p>
                                <p>
                                  <span className="font-semibold text-slate-450">Published:</span> {fd.advisedDate || "N/A"} by {d.saved_by}
                                </p>
                                {fd.notes && (
                                  <p className="text-slate-500 italic mt-1 bg-slate-50 px-2 py-1 rounded max-w-xl text-[10px] border border-slate-100">
                                    "{fd.notes}"
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 sm:self-center">
                              <Button
                                onClick={() => handleToggleExpand(d)}
                                variant="outline"
                                className="h-8 text-[11px] gap-1 px-3 border-slate-200"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                {expandedId === d.id ? "Hide Statement" : "View Statement"}
                              </Button>
                              <Button
                                onClick={() => handleDownload(fd)}
                                className={`h-8 text-[11px] gap-1 px-3 ${brandObj.bgButton} text-white`}
                              >
                                <Download className="w-3.5 h-3.5" />
                                Download PDF
                              </Button>
                              {isAdminMode && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleDelete(d.id)}
                                  className="w-8 h-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg flex-shrink-0"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Expanded PDF Preview Panel */}
                          {expandedId === d.id && (
                            <div className="border border-slate-150 rounded-lg overflow-hidden bg-slate-50/60 p-1.5 animate-fade-in">
                              {previewUrl ? (
                                <iframe
                                  src={previewUrl}
                                  className="w-full h-[650px] border-none rounded bg-white shadow-inner"
                                  title="Statement PDF Preview"
                                />
                              ) : (
                                <div className="h-[200px] flex items-center justify-center text-xs text-slate-455 font-semibold">
                                  Generating PDF Preview...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isAdminMode && (
            <TabsContent value="payments" className="mt-0">
            {/* Payments made: what to send the firm. Built only from payments recorded on
                this screen, which is the only place a payment date and amount exist. */}
                <Card className="shadow-sm border-slate-205">
                  <CardHeader className="py-4 border-b border-slate-100">
                    <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-emerald-600" />
                      Payments made
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-500">
                      Payments recorded for {brandObj.orgName}, to print or save as a PDF and send to the firm.
                    </CardDescription>
                  </CardHeader>

                  <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
                    <Select value={reportPeriod} onValueChange={setReportPeriod}>
                      <SelectTrigger className="h-8 w-[140px] bg-white text-xs" aria-label="Report period">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_PERIODS.map(option => (
                          <SelectItem key={option.value} value={option.value} className="text-xs">{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setReportAnchor(shiftPeriod(reportPeriod, reportAnchor, -1))}
                      aria-label="Previous period"
                      className="h-8 px-2 text-xs"
                    >
                      ‹
                    </Button>
                    <Input
                      type="date"
                      value={reportAnchor}
                      onChange={event => setReportAnchor(event.target.value || todayIso())}
                      aria-label="Period starting"
                      className="h-8 w-[150px] bg-white text-xs"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setReportAnchor(shiftPeriod(reportPeriod, reportAnchor, 1))}
                      aria-label="Next period"
                      className="h-8 px-2 text-xs"
                    >
                      ›
                    </Button>
                    {["weekly", "fortnightly"].includes(reportPeriod) && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setReportAnchor(startOfWeek(reportAnchor))}
                        className="h-8 px-2 text-[11px] text-slate-500"
                      >
                        Start on Monday
                      </Button>
                    )}
                    <span className="text-[11px] font-semibold text-slate-700">{reportRange.label}</span>
                    <div className="ml-auto flex items-center gap-2">
                      {paymentsError && (
                        <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                          Payments could not be loaded
                        </span>
                      )}
                      <Button onClick={handlePrintPaymentsReport} variant="outline" className="h-8 gap-2 text-xs">
                        <Printer className="h-4 w-4" />
                        Print / Save as PDF
                      </Button>
                    </div>
                  </div>

                  <CardContent className="p-0">
                    {reportPayments.length === 0 ? (
                      <div className="p-12 text-center text-xs font-semibold text-slate-450">
                        No payments recorded in this period. Record one by marking an invoice Paid above.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-[11px]">
                          <thead className="bg-slate-100/80 text-[10px] uppercase tracking-wide text-slate-700">
                            <tr>
                              <th className="px-4 py-2 font-bold">Date paid</th>
                              <th className="px-4 py-2 font-bold">Client</th>
                              <th className="px-4 py-2 font-bold">Sent by</th>
                              <th className="px-4 py-2 font-bold">Type</th>
                              <th className="px-4 py-2 font-bold">Invoice</th>
                              <th className="px-4 py-2 text-right font-bold">Amount paid</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {reportPayments.map(paid => (
                              <tr key={paid.id} className="hover:bg-slate-50/60">
                                <td className="px-4 py-2 whitespace-nowrap">{formatDay(paid.paid_on)}</td>
                                <td className="px-4 py-2 font-semibold text-slate-800">{paid.client_name || "—"}</td>
                                <td className="px-4 py-2">{paid.sent_by || "—"}</td>
                                <td className="px-4 py-2">{paymentTypeLabel(paid.payment_type)}</td>
                                <td className="px-4 py-2">{paid.invoice_ref || "—"}</td>
                                <td className="px-4 py-2 text-right">{formatMoney(paid.amount_paid)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="text-[11px]">
                            <tr className="border-t border-slate-200 text-slate-600">
                              <td className="px-4 py-2" colSpan={5}>Fee slips</td>
                              <td className="px-4 py-2 text-right">{formatMoney(reportTotals.fee_slip)}</td>
                            </tr>
                            <tr className="text-slate-600">
                              <td className="px-4 py-2" colSpan={5}>Disbursement invoices</td>
                              <td className="px-4 py-2 text-right">{formatMoney(reportTotals.disbursement)}</td>
                            </tr>
                            <tr className="bg-slate-800 font-bold text-white">
                              <td className="px-4 py-2" colSpan={5}>
                                Total paid — {reportTotals.count} payment{reportTotals.count === 1 ? "" : "s"}
                              </td>
                              <td className="px-4 py-2 text-right">{formatMoney(reportTotals.total)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
            </TabsContent>
          )}

          <TabsContent value="recover" className="mt-0">
            <BudgetRecoupPanel
              brandPrefix={brandObj.appPrefix}
              recoup={recoup}
              screenRows={allCounselInvoices}
              userName={recoupUserName}
            />
          </TabsContent>

          <TabsContent value="claims" className="mt-0">
            <ClaimReceiptsFolder
              store={claimReceipts}
              candidates={claimCandidates}
              itemWord="fee slip"
              brand={brandType}
              canEdit={isAdminMode || user?.role === "admin"}
              userName={recoupUserName}
            />
          </TabsContent>
        </Tabs>

        {/* ADD: one form at a time, in a side panel. */}
        {isAdminMode && (
          <Sheet open={Boolean(addForm)} onOpenChange={(open) => { if (!open) setAddForm(null); }}>
            <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
              <SheetHeader className="sr-only">
                <SheetTitle>
                  {addForm === "fee_slip" ? "Add a fee slip" : addForm === "statement" ? "Upload a clerk statement" : "Upload a paid summary"}
                </SheetTitle>
                <SheetDescription>Saved for {brandObj.orgName}.</SheetDescription>
              </SheetHeader>
              <div className="space-y-4 pt-4">
                {addForm === "paid_summary" && (
                  <>
                    <div className="flex justify-end">
                      <CloudDraftsPanel
                        formType="fee_slip_invoice_payment"
                        formData={{ statementType, selectedClerk, selectedClerkName, selectedMonth, selectedYear, notes }}
                        onLoadDraft={(draft) => {
                          if (!draft) return;
                          if (draft.statementType) setStatementType(draft.statementType);
                          setSelectedClerk(draft.selectedClerk ?? "");
                          setSelectedClerkName(draft.selectedClerkName ?? "");
                          if (draft.selectedMonth) setSelectedMonth(draft.selectedMonth);
                          if (draft.selectedYear) setSelectedYear(draft.selectedYear);
                          setNotes(draft.notes ?? "");
                        }}
                        brand={brandType}
                      />
                    </div>
                    <Card className="shadow-sm border-slate-205">
                      <CardHeader className={`bg-gradient-to-r ${brandObj.bgHeader} text-white rounded-t-lg py-4`}>
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <Upload className="w-5 h-5" />
                          Upload Paid Summary
                        </CardTitle>
                        <CardDescription className="text-teal-100 text-xs">
                          Advise lawyers what has been paid by uploading a PDF summary sheet.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-6 space-y-5">
                        <form onSubmit={handleSubmit} className="space-y-4">
                    
                          {/* Category Selection */}
                          <div>
                            <Label className="text-xs font-semibold text-slate-700">Statement Type</Label>
                            <Select value={statementType} onValueChange={setStatementType}>
                              <SelectTrigger className="mt-1.5 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="clerk_fee_slip" className="text-xs">Clerk Fee Slip PDF</SelectItem>
                                <SelectItem value="invoice_payment" className="text-xs">Invoice Paid PDF</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Clerk Selection */}
                          {statementType === "clerk_fee_slip" && (
                            <div>
                              <Label className="text-xs font-semibold text-slate-700">Clerk / Bar List</Label>
                              <Select value={selectedClerk} onValueChange={setSelectedClerk}>
                                <SelectTrigger className="mt-1.5 text-xs">
                                  <SelectValue placeholder="Select Clerk" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAID_SUMMARY_CLERKS.filter(c => c !== DIRECT_INVOICE_PAYMENTS).map((c) => (
                                    <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Statement Period */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs font-semibold text-slate-700">Paid Month</Label>
                              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger className="mt-1.5 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {MONTHS.map((m) => (
                                    <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs font-semibold text-slate-700">Paid Year</Label>
                              <Select value={selectedYear} onValueChange={setSelectedYear}>
                                <SelectTrigger className="mt-1.5 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {YEARS.map((y) => (
                                    <SelectItem key={y} value={y} className="text-xs">{y}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Drag and Drop */}
                          <div>
                            <Label className="text-xs font-semibold text-slate-700">Statement PDF File</Label>
                            <div 
                              onDragOver={handleDragOver}
                              onDragLeave={handleDragLeave}
                              onDrop={handleDrop}
                              onClick={() => document.getElementById("summary-file-upload").click()}
                              className={`mt-1.5 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${
                                isDragging ? "border-teal-700 bg-teal-50/50" : "border-slate-350 hover:border-slate-400 bg-slate-50/30"
                              }`}
                            >
                              <input 
                                type="file" 
                                id="summary-file-upload" 
                                accept=".pdf"
                                className="hidden" 
                                onChange={handleFileSelect}
                              />
                              <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                              {attachedFile ? (
                                <div className="space-y-1">
                                  <p className="text-xs font-semibold text-teal-750 break-all">{attachedFile.name}</p>
                                  <p className="text-[10px] text-slate-400">{(attachedFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <p className="text-xs font-semibold text-slate-600">Drag & Drop Summary PDF here</p>
                                  <p className="text-[10px] text-slate-400">or click to browse local files (PDF only)</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Notes */}
                          <div>
                            <Label className="text-xs font-semibold text-slate-700">Remarks / Summary Details (Optional)</Label>
                            <Textarea 
                              value={notes} 
                              onChange={e => setNotes(e.target.value)} 
                              placeholder="Add processing details, month notes or instructions..."
                              className="mt-1.5 text-xs"
                              rows={2}
                            />
                          </div>

                          {/* Submit */}
                          <Button 
                            type="submit" 
                            className={`w-full ${brandObj.bgButton} text-white font-semibold text-xs py-2 rounded shadow-sm transition`}
                            disabled={isLoading || isUploading}
                          >
                            {isLoading ? "Publishing..." : "Upload & Advise Lawyers"}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>
                  </>
                )}
                {addForm === "statement" && (
                  <ClerkStatementUploadCard
                    brand={brandType}
                    headerClass={brandObj.bgHeader}
                    buttonClass={brandObj.bgButton}
                    onSaved={() => { fetchClerkStatementRows(); setAddForm(null); }}
                  />
                )}
                {addForm === "fee_slip" && (
                  <AddFeeSlipCard
                    apiBase={DEADLINE_API_BASE}
                    brand={brandType}
                    headerClass={brandObj.bgHeader}
                    buttonClass={brandObj.bgButton}
                    onSaved={() => { fetchIndividualFeeSlips(); setAddForm(null); }}
                  />
                )}
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}
